import {
  PerformanceLabPlan,
  PerformanceLabSection,
  PerfLabOffer,
  PlanIncludeDetail,
} from './types';

// ─── SHARED OFFERS ──────────────────────────────────────────────────────────
// Reusable promo objects. Attach an `offer` to any plan to surface a limited
// time price; the regular `price` is kept and shown struck through.

import { MONSOON_OFFER_ENDS } from './planOffer';
export { MONSOON_OFFER_ENDS };

const monsoonPercentOffer = (price: number): PerfLabOffer => ({
  offerPrice: Math.round(price * 0.9),
  expiresAt: MONSOON_OFFER_ENDS,
  discountCode: 'MONSOON10',
});

// Shopify list is already ₹2,499 / ₹3,999; compare-at stays as the struck MSRP
// when the festive cut is off. While EXINPL15 is active, priceCut.ts wins.
const permanentMassageOffer: PerfLabOffer = {
  offerPrice: 2499,
  strikePrice: 3499,
};

const permanentCryoOffer: PerfLabOffer = {
  offerPrice: 3999,
  strikePrice: 5999,
};

const PERMANENT_FLAT_OFFERS: Record<string, PerfLabOffer> = {
  'pl-standalone-massage-male': permanentMassageOffer,
  'pl-standalone-massage-female': permanentMassageOffer,
  'pl-standalone-cryo': permanentCryoOffer,
};

/** Recovery, Basic, and Gold are excluded from the monsoon % offer. */
const MONSOON_OFFER_EXCLUDED_IDS = new Set([
  'diagnostics-basic',
  'recovery-day-pass',
  'recovery-month-pass',
  'recovery-3-month-pass',
  'gold-pro',
  'gold-elite',
]);

const applyPlanOffers = (plan: PerformanceLabPlan): PerformanceLabPlan => {
  if (plan.price === 0) {
    return plan;
  }
  const permanentOffer = PERMANENT_FLAT_OFFERS[plan.id];
  if (permanentOffer) {
    return { ...plan, offer: permanentOffer };
  }
  if (MONSOON_OFFER_EXCLUDED_IDS.has(plan.id)) {
    return plan;
  }
  return { ...plan, offer: monsoonPercentOffer(plan.price) };
};

const withMonsoonOffers = (
  sections: PerformanceLabSection[],
): PerformanceLabSection[] =>
  sections.map((section) => ({
    ...section,
    plans: section.plans.map(applyPlanOffers),
    subcategories: section.subcategories?.map((sub) => ({
      ...sub,
      plans: sub.plans.map(applyPlanOffers),
    })),
  }));

// ─── SHARED RECOVERY DETAILS ────────────────────────────────────────────────
// Extracted to avoid triplication across Day/Month/3-Month passes.

const recoveryInfraredSaunaDetail: PlanIncludeDetail = {
  thumbnailUrl:
    'web_v2/performance-lab/pricing-page/placeholders/diagnostics/recovery-services.png',
  imageUrl:
    'web_v2/performance-lab/pricing-page/placeholders/sidebar/infrared-sauna-3.png',
  description: 'details.infraredSauna.description',
  sections: [
    {
      title: 'details.infraredSauna.sections.0.title',
      content: 'details.infraredSauna.sections.0.content',
    },
    {
      title: 'details.infraredSauna.sections.1.title',
      content: 'details.infraredSauna.sections.1.content',
    },
    {
      title: 'details.infraredSauna.sections.2.title',
      content: 'details.infraredSauna.sections.2.content',
    },
  ],
};

const recoveryColdPlungeDetail: PlanIncludeDetail = {
  thumbnailUrl:
    'web_v2/performance-lab/pricing-page/placeholders/diagnostics/recovery-services.png',
  imageUrl:
    'web_v2/performance-lab/pricing-page/placeholders/sidebar/cold-plunge-3.png',
  description: 'details.coldPlunge.description',
  sections: [
    {
      title: 'details.coldPlunge.sections.0.title',
      content: 'details.coldPlunge.sections.0.content',
    },
    {
      title: 'details.coldPlunge.sections.1.title',
      content: 'details.coldPlunge.sections.1.content',
    },
    {
      title: 'details.coldPlunge.sections.2.title',
      content: 'details.coldPlunge.sections.2.content',
    },
  ],
};

const recoveryDecompressionDetail: PlanIncludeDetail = {
  thumbnailUrl:
    'web_v2/performance-lab/pricing-page/placeholders/diagnostics/recovery-services.png',
  imageUrl:
    'web_v2/performance-lab/pricing-page/placeholders/sidebar/inversion-2.png',
  description: 'details.decompression.description',
  sections: [
    {
      title: 'details.decompression.sections.0.title',
      content: 'details.decompression.sections.0.content',
    },
    {
      title: 'details.decompression.sections.1.title',
      content: 'details.decompression.sections.1.content',
    },
    {
      title: 'details.decompression.sections.2.title',
      content: 'details.decompression.sections.2.content',
    },
  ],
};

const recoveryPowerplateDetail: PlanIncludeDetail = {
  thumbnailUrl:
    'web_v2/performance-lab/pricing-page/placeholders/diagnostics/recovery-services.png',
  imageUrl:
    'web_v2/performance-lab/pricing-page/placeholders/sidebar/powerplate-2.png',
  description: 'details.powerplate.description',
  sections: [
    {
      title: 'details.powerplate.sections.0.title',
      content: 'details.powerplate.sections.0.content',
    },
    {
      title: 'details.powerplate.sections.1.title',
      content: 'details.powerplate.sections.1.content',
    },
    {
      title: 'details.powerplate.sections.2.title',
      content: 'details.powerplate.sections.2.content',
    },
  ],
};

const recoveryCompressionDetail: PlanIncludeDetail = {
  thumbnailUrl:
    'web_v2/performance-lab/pricing-page/placeholders/diagnostics/recovery-services.png',
  imageUrl:
    'web_v2/performance-lab/pricing-page/placeholders/sidebar/compression-3.png',
  description: 'details.compression.description',
  sections: [
    {
      title: 'details.compression.sections.0.title',
      content: 'details.compression.sections.0.content',
    },
    {
      title: 'details.compression.sections.1.title',
      content: 'details.compression.sections.1.content',
    },
    {
      title: 'details.compression.sections.2.title',
      content: 'details.compression.sections.2.content',
    },
  ],
};

const recoveryMassageGunsDetail: PlanIncludeDetail = {
  thumbnailUrl:
    'web_v2/performance-lab/pricing-page/placeholders/diagnostics/recovery-services.png',
  imageUrl:
    'web_v2/performance-lab/pricing-page/placeholders/sidebar/massage-guns-1.png',
  description: 'details.massageGuns.description',
  sections: [
    {
      title: 'details.massageGuns.sections.0.title',
      content: 'details.massageGuns.sections.0.content',
    },
    {
      title: 'details.massageGuns.sections.1.title',
      content: 'details.massageGuns.sections.1.content',
    },
    {
      title: 'details.massageGuns.sections.2.title',
      content: 'details.massageGuns.sections.2.content',
    },
  ],
};

const recoveryLocalizedHotColdDetail: PlanIncludeDetail = {
  thumbnailUrl:
    'web_v2/performance-lab/pricing-page/placeholders/diagnostics/recovery-services.png',
  imageUrl:
    'web_v2/performance-lab/pricing-page/placeholders/sidebar/localised-hot-cold-therapy-2.png',
  description: 'details.localizedHotCold.description',
  sections: [
    {
      title: 'details.localizedHotCold.sections.0.title',
      content: 'details.localizedHotCold.sections.0.content',
    },
    {
      title: 'details.localizedHotCold.sections.1.title',
      content: 'details.localizedHotCold.sections.1.content',
    },
    {
      title: 'details.localizedHotCold.sections.2.title',
      content: 'details.localizedHotCold.sections.2.content',
    },
  ],
};

const recoveryHBOTDetail: PlanIncludeDetail = {
  thumbnailUrl:
    'web_v2/performance-lab/pricing-page/placeholders/standalone/hyperbaric-oxygen.png',
  imageUrl:
    'web_v2/performance-lab/pricing-page/placeholders/sidebar/hyperbaric-oxygen-therapy-(hbot)-2.png',
  description: 'details.hbot.description',
  sections: [
    {
      title: 'details.hbot.sections.0.title',
      content: 'details.hbot.sections.0.content',
    },
    {
      title: 'details.hbot.sections.1.title',
      content: 'details.hbot.sections.1.content',
    },
    {
      title: 'details.hbot.sections.2.title',
      content: 'details.hbot.sections.2.content',
    },
  ],
};

const recoverySportsMassageDetail: PlanIncludeDetail = {
  thumbnailUrl:
    'web_v2/performance-lab/pricing-page/placeholders/standalone/sports-massage.png',
  imageUrl:
    'web_v2/performance-lab/pricing-page/placeholders/sidebar/sports-massage-2.png',
  description: 'details.sportsMassage.description',
  sections: [
    {
      title: 'details.sportsMassage.sections.0.title',
      content: 'details.sportsMassage.sections.0.content',
    },
    {
      title: 'details.sportsMassage.sections.1.title',
      content: 'details.sportsMassage.sections.1.content',
    },
    {
      title: 'details.sportsMassage.sections.2.title',
      content: 'details.sportsMassage.sections.2.content',
    },
  ],
};

const recoveryCryotherapyDetail: PlanIncludeDetail = {
  thumbnailUrl:
    'web_v2/performance-lab/pricing-page/placeholders/standalone/cryotherapy.png',
  imageUrl:
    'web_v2/performance-lab/pricing-page/placeholders/sidebar/whole-body-cryotherapy-2.png',
  description: 'details.cryotherapy.description',
  sections: [
    {
      title: 'details.cryotherapy.sections.0.title',
      content: 'details.cryotherapy.sections.0.content',
    },
    {
      title: 'details.cryotherapy.sections.1.title',
      content: 'details.cryotherapy.sections.1.content',
    },
    {
      title: 'details.cryotherapy.sections.2.title',
      content: 'details.cryotherapy.sections.2.content',
    },
  ],
};

// ─── SHARED DIAGNOSTICS DETAILS (reused by Standalone Modules) ──────────────

const bloodVisionBaseDetail: PlanIncludeDetail = {
  thumbnailUrl:
    'web_v2/performance-lab/pricing-page/placeholders/diagnostics/blood-test.png',
  imageUrl:
    'web_v2/performance-lab/pricing-page/placeholders/sidebar/blood-vision-base.png',
  description: 'details.bloodVisionBase.description',
  sections: [
    {
      title: 'details.bloodVisionBase.sections.0.title',
      content: 'details.bloodVisionBase.sections.0.content',
    },
    {
      title: 'details.bloodVisionBase.sections.1.title',
      content: 'details.bloodVisionBase.sections.1.content',
    },
    {
      title: 'details.bloodVisionBase.sections.2.title',
      content: 'details.bloodVisionBase.sections.2.content',
    },
  ],
};

const bloodVisionPeakDetail: PlanIncludeDetail = {
  thumbnailUrl:
    'web_v2/performance-lab/pricing-page/placeholders/diagnostics/blood-test.png',
  imageUrl:
    'web_v2/performance-lab/pricing-page/placeholders/sidebar/blood-vision-peak.png',
  description: 'details.bloodVisionPeak.description',
  sections: [
    {
      title: 'details.bloodVisionPeak.sections.0.title',
      content: 'details.bloodVisionPeak.sections.0.content',
    },
    {
      title: 'details.bloodVisionPeak.sections.1.title',
      content: 'details.bloodVisionPeak.sections.1.content',
    },
    {
      title: 'details.bloodVisionPeak.sections.2.title',
      content: 'details.bloodVisionPeak.sections.2.content',
    },
  ],
};

const bodyCompositionDetail: PlanIncludeDetail = {
  thumbnailUrl:
    'web_v2/performance-lab/pricing-page/placeholders/diagnostics/body-composition-analysis.png',
  imageUrl:
    'web_v2/performance-lab/pricing-page/placeholders/sidebar/body-composition-analysis-3.png',
  description: 'details.bodyComposition.description',
  sections: [
    {
      title: 'details.bodyComposition.sections.0.title',
      content: 'details.bodyComposition.sections.0.content',
    },
    {
      title: 'details.bodyComposition.sections.1.title',
      content: 'details.bodyComposition.sections.1.content',
    },
    {
      title: 'details.bodyComposition.sections.2.title',
      content: 'details.bodyComposition.sections.2.content',
    },
  ],
};

const boneDensityDetail: PlanIncludeDetail = {
  thumbnailUrl:
    'web_v2/performance-lab/pricing-page/placeholders/diagnostics/bone-density.png',
  imageUrl:
    'web_v2/performance-lab/pricing-page/placeholders/sidebar/bone-density-3.png',
  description: 'details.boneDensity.description',
  sections: [
    {
      title: 'details.boneDensity.sections.0.title',
      content: 'details.boneDensity.sections.0.content',
    },
    {
      title: 'details.boneDensity.sections.1.title',
      content: 'details.boneDensity.sections.1.content',
    },
    {
      title: 'details.boneDensity.sections.2.title',
      content: 'details.boneDensity.sections.2.content',
    },
  ],
};

const ecgDetail: PlanIncludeDetail = {
  thumbnailUrl:
    'web_v2/performance-lab/pricing-page/placeholders/diagnostics/ecg.png',
  imageUrl:
    'web_v2/performance-lab/pricing-page/placeholders/sidebar/electrocardiogram-2.png',
  description: 'details.ecg.description',
  sections: [
    {
      title: 'details.ecg.sections.0.title',
      content: 'details.ecg.sections.0.content',
    },
    {
      title: 'details.ecg.sections.1.title',
      content: 'details.ecg.sections.1.content',
    },
    {
      title: 'details.ecg.sections.2.title',
      content: 'details.ecg.sections.2.content',
    },
  ],
};

const visionDetail: PlanIncludeDetail = {
  thumbnailUrl:
    'web_v2/performance-lab/pricing-page/placeholders/diagnostics/vision.png',
  imageUrl:
    'web_v2/performance-lab/pricing-page/placeholders/sidebar/vision-2.png',
  description: 'details.vision.description',
  sections: [
    {
      title: 'details.vision.sections.0.title',
      content: 'details.vision.sections.0.content',
    },
    {
      title: 'details.vision.sections.1.title',
      content: 'details.vision.sections.1.content',
    },
    {
      title: 'details.vision.sections.2.title',
      content: 'details.vision.sections.2.content',
    },
  ],
};

const hearingDetail: PlanIncludeDetail = {
  thumbnailUrl:
    'web_v2/performance-lab/pricing-page/placeholders/diagnostics/hearing.png',
  imageUrl:
    'web_v2/performance-lab/pricing-page/placeholders/sidebar/hearing-2.png',
  description: 'details.hearing.description',
  sections: [
    {
      title: 'details.hearing.sections.0.title',
      content: 'details.hearing.sections.0.content',
    },
    {
      title: 'details.hearing.sections.1.title',
      content: 'details.hearing.sections.1.content',
    },
    {
      title: 'details.hearing.sections.2.title',
      content: 'details.hearing.sections.2.content',
    },
  ],
};

const oralHealthDetail: PlanIncludeDetail = {
  thumbnailUrl:
    'web_v2/performance-lab/pricing-page/placeholders/diagnostics/oral-health.png',
  imageUrl:
    'web_v2/performance-lab/pricing-page/placeholders/sidebar/oral-health-2.png',
  description: 'details.oralHealth.description',
  sections: [
    {
      title: 'details.oralHealth.sections.0.title',
      content: 'details.oralHealth.sections.0.content',
    },
    {
      title: 'details.oralHealth.sections.1.title',
      content: 'details.oralHealth.sections.1.content',
    },
    {
      title: 'details.oralHealth.sections.2.title',
      content: 'details.oralHealth.sections.2.content',
    },
  ],
};

const cognitionDetail: PlanIncludeDetail = {
  thumbnailUrl:
    'web_v2/performance-lab/pricing-page/placeholders/diagnostics/cognition.png',
  imageUrl:
    'web_v2/performance-lab/pricing-page/placeholders/sidebar/cognition-2.png',
  description: 'details.cognition.description',
  sections: [
    {
      title: 'details.cognition.sections.0.title',
      content: 'details.cognition.sections.0.content',
    },
    {
      title: 'details.cognition.sections.1.title',
      content: 'details.cognition.sections.1.content',
    },
    {
      title: 'details.cognition.sections.2.title',
      content: 'details.cognition.sections.2.content',
    },
  ],
};

const gripStrengthDetail: PlanIncludeDetail = {
  thumbnailUrl:
    'web_v2/performance-lab/pricing-page/placeholders/diagnostics/musculoskeletal-assessment.png',
  imageUrl:
    'web_v2/performance-lab/pricing-page/placeholders/sidebar/grip-strength-1.png',
  description: 'details.gripStrength.description',
  sections: [
    {
      title: 'details.gripStrength.sections.0.title',
      content: 'details.gripStrength.sections.0.content',
    },
    {
      title: 'details.gripStrength.sections.1.title',
      content: 'details.gripStrength.sections.1.content',
    },
    {
      title: 'details.gripStrength.sections.2.title',
      content: 'details.gripStrength.sections.2.content',
    },
  ],
};

const lungHealthDetail: PlanIncludeDetail = {
  thumbnailUrl:
    'web_v2/performance-lab/pricing-page/placeholders/diagnostics/pulmonary-function-test.png',
  imageUrl:
    'web_v2/performance-lab/pricing-page/placeholders/sidebar/lung-health-2.png',
  description: 'details.lungHealth.description',
  sections: [
    {
      title: 'details.lungHealth.sections.0.title',
      content: 'details.lungHealth.sections.0.content',
    },
    {
      title: 'details.lungHealth.sections.1.title',
      content: 'details.lungHealth.sections.1.content',
    },
    {
      title: 'details.lungHealth.sections.2.title',
      content: 'details.lungHealth.sections.2.content',
    },
  ],
};

const vo2MaxTreadmillDetail: PlanIncludeDetail = {
  thumbnailUrl:
    'web_v2/performance-lab/pricing-page/placeholders/diagnostics/vo2-max.png',
  imageUrl:
    'web_v2/performance-lab/pricing-page/placeholders/sidebar/vo₂-max-2.png',
  description: 'details.vo2Max.description',
  sections: [
    {
      title: 'details.vo2Max.sections.0.title',
      content: 'details.vo2Max.sections.0.content',
    },
    {
      title: 'details.vo2Max.sections.1.title',
      content: 'details.vo2Max.sections.1.content',
    },
    {
      title: 'details.vo2Max.sections.2.title',
      content: 'details.vo2Max.sections.2.content',
    },
  ],
};

const vo2MaxBikeDetail: PlanIncludeDetail = {
  thumbnailUrl:
    'web_v2/performance-lab/pricing-page/placeholders/diagnostics/vo2-max.png',
  imageUrl:
    'web_v2/performance-lab/pricing-page/placeholders/sidebar/vo₂-max-bike-2.png',
  description: 'details.vo2Max.description',
  sections: [
    {
      title: 'details.vo2Max.sections.0.title',
      content: 'details.vo2Max.sections.0.content',
    },
    {
      title: 'details.vo2Max.sections.1.title',
      content: 'details.vo2Max.sections.1.content',
    },
    {
      title: 'details.vo2Max.sections.2.title',
      content: 'details.vo2Max.sections.2.content',
    },
  ],
};

const musculoskeletalBasicDetail: PlanIncludeDetail = {
  thumbnailUrl:
    'web_v2/performance-lab/pricing-page/placeholders/diagnostics/musculoskeletal-assessment.png',
  imageUrl:
    'web_v2/performance-lab/pricing-page/placeholders/sidebar/musculoskeletal-assessment-(basic)-2.png',
  description: 'details.musculoskeletalBasic.description',
  sections: [
    {
      title: 'details.musculoskeletalBasic.sections.0.title',
      content: 'details.musculoskeletalBasic.sections.0.content',
    },
    {
      title: 'details.musculoskeletalBasic.sections.1.title',
      content: 'details.musculoskeletalBasic.sections.1.content',
    },
    {
      title: 'details.musculoskeletalBasic.sections.2.title',
      content: 'details.musculoskeletalBasic.sections.2.content',
    },
  ],
};

const musculoskeletalProDetail: PlanIncludeDetail = {
  thumbnailUrl:
    'web_v2/performance-lab/pricing-page/placeholders/diagnostics/musculoskeletal-assessment.png',
  imageUrl:
    'web_v2/performance-lab/pricing-page/placeholders/sidebar/musculoskeletal-assessment-(basic)-2.png',
  description: 'details.musculoskeletalPro.description',
  sections: [
    {
      title: 'details.musculoskeletalPro.sections.0.title',
      content: 'details.musculoskeletalPro.sections.0.content',
    },
    {
      title: 'details.musculoskeletalPro.sections.1.title',
      content: 'details.musculoskeletalPro.sections.1.content',
    },
    {
      title: 'details.musculoskeletalPro.sections.2.title',
      content: 'details.musculoskeletalPro.sections.2.content',
    },
  ],
};

const nutritionDetail: PlanIncludeDetail = {
  thumbnailUrl:
    'web_v2/performance-lab/pricing-page/placeholders/diagnostics/nutrition.png',
  imageUrl:
    'web_v2/performance-lab/pricing-page/placeholders/sidebar/nutrition-2.png',
  description: 'details.nutrition.description',
  sections: [
    {
      title: 'details.nutrition.sections.0.title',
      content: 'details.nutrition.sections.0.content',
    },
    {
      title: 'details.nutrition.sections.1.title',
      content: 'details.nutrition.sections.1.content',
    },
    {
      title: 'details.nutrition.sections.2.title',
      content: 'details.nutrition.sections.2.content',
    },
  ],
};

const nutriDNADetail: PlanIncludeDetail = {
  thumbnailUrl:
    'web_v2/performance-lab/pricing-page/placeholders/diagnostics/genetic-testing.png',
  imageUrl:
    'web_v2/performance-lab/pricing-page/placeholders/sidebar/genetics-2.png',
  description: 'details.nutriDNA.description',
  sections: [
    {
      title: 'details.nutriDNA.sections.0.title',
      content: 'details.nutriDNA.sections.0.content',
    },
    {
      title: 'details.nutriDNA.sections.1.title',
      content: 'details.nutriDNA.sections.1.content',
    },
    {
      title: 'details.nutriDNA.sections.2.title',
      content: 'details.nutriDNA.sections.2.content',
    },
  ],
};

const genomicHealthDetail: PlanIncludeDetail = {
  thumbnailUrl:
    'web_v2/performance-lab/pricing-page/placeholders/diagnostics/genetic-testing.png',
  imageUrl:
    'web_v2/performance-lab/pricing-page/placeholders/sidebar/genetics-2.png',
  description: 'details.genomicHealth.description',
  sections: [
    {
      title: 'details.genomicHealth.sections.0.title',
      content: 'details.genomicHealth.sections.0.content',
    },
    {
      title: 'details.genomicHealth.sections.1.title',
      content: 'details.genomicHealth.sections.1.content',
    },
    {
      title: 'details.genomicHealth.sections.2.title',
      content: 'details.genomicHealth.sections.2.content',
    },
  ],
};

const gutMicrobiomeDetail: PlanIncludeDetail = {
  thumbnailUrl:
    'web_v2/performance-lab/pricing-page/placeholders/diagnostics/gut-microbiome.png',
  imageUrl:
    'web_v2/performance-lab/pricing-page/placeholders/sidebar/gut-microbiome-2.png',
  description: 'details.gutMicrobiome.description',
  sections: [
    {
      title: 'details.gutMicrobiome.sections.0.title',
      content: 'details.gutMicrobiome.sections.0.content',
    },
    {
      title: 'details.gutMicrobiome.sections.1.title',
      content: 'details.gutMicrobiome.sections.1.content',
    },
    {
      title: 'details.gutMicrobiome.sections.2.title',
      content: 'details.gutMicrobiome.sections.2.content',
    },
  ],
};

const sleepStudyDetail: PlanIncludeDetail = {
  thumbnailUrl:
    'web_v2/performance-lab/pricing-page/placeholders/diagnostics/sleep_study_polysomnography.png',
  imageUrl:
    'web_v2/performance-lab/pricing-page/placeholders/sidebar/sleep-study-polysomnography.png',
  description: 'details.sleepStudy.description',
  sections: [
    {
      title: 'details.sleepStudy.sections.0.title',
      content: 'details.sleepStudy.sections.0.content',
    },
    {
      title: 'details.sleepStudy.sections.1.title',
      content: 'details.sleepStudy.sections.1.content',
    },
    {
      title: 'details.sleepStudy.sections.2.title',
      content: 'details.sleepStudy.sections.2.content',
    },
  ],
};

const biointelligenceAiDetail: PlanIncludeDetail = {
  thumbnailUrl:
    'web_v2/performance-lab/pricing-page/placeholders/diagnostics/bio-ai.png',
  description: 'details.biointelligenceAi.description',
  sections: [
    {
      content: 'details.biointelligenceAi.sections.0.content',
    },
    {
      title: 'details.biointelligenceAi.sections.1.title',
      content: 'details.biointelligenceAi.sections.1.content',
    },
    {
      title: 'details.biointelligenceAi.sections.2.title',
      content: 'details.biointelligenceAi.sections.2.content',
    },
    {
      title: 'details.biointelligenceAi.sections.3.title',
      content: 'details.biointelligenceAi.sections.3.content',
    },
  ],
};

const systemWisePerformanceScoreDetail: PlanIncludeDetail = {
  thumbnailUrl:
    'web_v2/performance-lab/pricing-page/placeholders/diagnostics/sys-perf.png',
  description: 'details.systemWisePerformanceScore.description',
  sections: [
    {
      content: 'details.systemWisePerformanceScore.sections.0.content',
    },
    {
      title: 'details.systemWisePerformanceScore.sections.1.title',
      content: 'details.systemWisePerformanceScore.sections.1.content',
    },
    {
      title: 'details.systemWisePerformanceScore.sections.2.title',
      content: 'details.systemWisePerformanceScore.sections.2.content',
    },
    {
      title: 'details.systemWisePerformanceScore.sections.3.title',
      content: 'details.systemWisePerformanceScore.sections.3.content',
    },
  ],
};

const actionPlanDetail: PlanIncludeDetail = {
  thumbnailUrl:
    'web_v2/performance-lab/pricing-page/placeholders/diagnostics/action-plan.png',
  description: 'details.actionPlan.description',
  sections: [
    {
      content: 'details.actionPlan.sections.0.content',
    },
    {
      title: 'details.actionPlan.sections.1.title',
      content: 'details.actionPlan.sections.1.content',
    },
    {
      title: 'details.actionPlan.sections.2.title',
      content: 'details.actionPlan.sections.2.content',
    },
    {
      title: 'details.actionPlan.sections.3.title',
      content: 'details.actionPlan.sections.3.content',
    },
  ],
};

export const extraModalDetails: PlanIncludeDetail[] = [
  biointelligenceAiDetail,
  systemWisePerformanceScoreDetail,
  actionPlanDetail,
  musculoskeletalBasicDetail,
  sleepStudyDetail,
];

const vitalsDetail: PlanIncludeDetail = {
  thumbnailUrl:
    'web_v2/performance-lab/pricing-page/placeholders/diagnostics/vitals.png',
  imageUrl:
    'web_v2/performance-lab/pricing-page/placeholders/sidebar/vitals-2.png',
  description: 'details.vitals.description',
  sections: [
    {
      title: 'details.vitals.sections.0.title',
      content: 'details.vitals.sections.0.content',
    },
    {
      title: 'details.vitals.sections.1.title',
      content: 'details.vitals.sections.1.content',
    },
    {
      title: 'details.vitals.sections.2.title',
      content: 'details.vitals.sections.2.content',
    },
  ],
};

const tScoreZScoreDetail: PlanIncludeDetail = {
  thumbnailUrl:
    'web_v2/performance-lab/pricing-page/placeholders/diagnostics/bone-density.png',
  description: 'details.tScoreZScore.description',
  sections: [
    {
      title: 'details.tScoreZScore.sections.0.title',
      content: 'details.tScoreZScore.sections.0.content',
    },
    {
      title: 'details.tScoreZScore.sections.1.title',
      content: 'details.tScoreZScore.sections.1.content',
    },
    {
      title: 'details.tScoreZScore.sections.2.title',
      content: 'details.tScoreZScore.sections.2.content',
    },
  ],
};

const personalizedRoadmapDetail: PlanIncludeDetail = {
  thumbnailUrl:
    'web_v2/performance-lab/pricing-page/placeholders/diagnostics/doctor-consultation.png',
  description: 'details.personalizedRoadmap.description',
  sections: [
    {
      title: 'details.personalizedRoadmap.sections.0.title',
      content: 'details.personalizedRoadmap.sections.0.content',
    },
    {
      title: 'details.personalizedRoadmap.sections.1.title',
      content: 'details.personalizedRoadmap.sections.1.content',
    },
    {
      title: 'details.personalizedRoadmap.sections.2.title',
      content: 'details.personalizedRoadmap.sections.2.content',
    },
  ],
};

const visionDashboardDetail: PlanIncludeDetail = {
  thumbnailUrl:
    'web_v2/performance-lab/pricing-page/placeholders/diagnostics/doctor-consultation.png',
  imageUrl:
    'web_v2/performance-lab/pricing-page/placeholders/sidebar/vision-dashboard.png',
  description: '',
  sections: [
    {
      content: 'details.visionDashboard.sections.0.content',
    },
    {
      content: 'details.visionDashboard.sections.1.content',
    },
    {
      content: 'details.visionDashboard.sections.2.content',
    },
  ],
};

const hearingVisionDetail: PlanIncludeDetail = {
  thumbnailUrl:
    'web_v2/performance-lab/pricing-page/placeholders/diagnostics/vision.png',
  imageUrl:
    'web_v2/performance-lab/pricing-page/placeholders/sidebar/vision.png',
  description: 'details.hearingVision.description',
  sections: [
    {
      title: 'details.hearingVision.sections.0.title',
      content: 'details.hearingVision.sections.0.content',
    },
    {
      title: 'details.hearingVision.sections.1.title',
      content: 'details.hearingVision.sections.1.content',
    },
    {
      title: 'details.hearingVision.sections.2.title',
      content: 'details.hearingVision.sections.2.content',
    },
    {
      title: 'details.hearingVision.sections.3.title',
      content: 'details.hearingVision.sections.3.content',
    },
    {
      title: 'details.hearingVision.sections.4.title',
      content: 'details.hearingVision.sections.4.content',
    },
    {
      title: 'details.hearingVision.sections.5.title',
      content: 'details.hearingVision.sections.5.content',
    },
  ],
};

const doctorConsultationDetail: PlanIncludeDetail = {
  thumbnailUrl:
    'web_v2/performance-lab/pricing-page/placeholders/diagnostics/doctor-consultation.png',
  imageUrl:
    'web_v2/performance-lab/pricing-page/placeholders/sidebar/doctor-consultation-2.png',
  description: 'details.doctorConsultation.description',
  sections: [
    {
      title: 'details.doctorConsultation.sections.0.title',
      content: 'details.doctorConsultation.sections.0.content',
    },
    {
      title: 'details.doctorConsultation.sections.1.title',
      content: 'details.doctorConsultation.sections.1.content',
    },
    {
      title: 'details.doctorConsultation.sections.2.title',
      content: 'details.doctorConsultation.sections.2.content',
    },
  ],
};

const recoveryPassDetail: PlanIncludeDetail = {
  thumbnailUrl:
    'web_v2/performance-lab/pricing-page/placeholders/diagnostics/recovery-services.png',
  imageUrl:
    'web_v2/performance-lab/pricing-page/placeholders/sidebar/recovery-pass-2.png',
  description: 'details.recoveryPass.description',
  sections: [
    {
      title: 'details.recoveryPass.sections.0.title',
      content: 'details.recoveryPass.sections.0.content',
    },
    {
      title: 'details.recoveryPass.sections.1.title',
      content: 'details.recoveryPass.sections.1.content',
    },
    {
      title: 'details.recoveryPass.sections.2.title',
      content: 'details.recoveryPass.sections.2.content',
    },
  ],
};

const planSectionsBase: PerformanceLabSection[] = [
  // ─── 1. DIAGNOSTICS ───────────────────────────────────────────────
  {
    id: 'diagnostics',
    sidebarLabel: 'sections.diagnostics.sidebarLabel',
    heroTitle: 'sections.diagnostics.heroTitle',
    heroImageUrl:
      'web_v2/performance-lab/pricing-page/placeholders/heroes/diagnostic-1.png',
    heroDescription: 'sections.diagnostics.heroDescription',
    heroTextPosition: 'left',
    cardLayout: '3-col',
    compareHeaders: [
      'sections.diagnostics.compareHeaders.0',
      'sections.diagnostics.compareHeaders.1',
      'sections.diagnostics.compareHeaders.2',
    ],
    plans: [
      {
        id: 'diagnostics-basic',
        name: 'sections.diagnostics.plans.diagnostics-basic.name',
        description: 'sections.diagnostics.plans.diagnostics-basic.description',
        price: 9999,
        buttonLabel: 'sections.diagnostics.plans.diagnostics-basic.buttonLabel',
        buttonVariant: 'filled',
        thumbnailUrl:
          'web_v2/performance-lab/pricing-page/placeholders/diagnostics/basic.png',
        shopifyVariantId:
          process.env['NEXT_PUBLIC_ENVIRONMENT'] === 'PRODUCTION'
            ? '44742040092742'
            : '55543513808969',
        includesHeader:
          'sections.diagnostics.plans.diagnostics-basic.includesHeader',
        includes: [
          {
            label:
              'sections.diagnostics.plans.diagnostics-basic.includes.0.label',
            detail: visionDashboardDetail,
          },
          {
            label:
              'sections.diagnostics.plans.diagnostics-basic.includes.1.label',
            detail: bloodVisionBaseDetail,
          },
          {
            label:
              'sections.diagnostics.plans.diagnostics-basic.includes.2.label',
            detail: bodyCompositionDetail,
          },
          {
            label:
              'sections.diagnostics.plans.diagnostics-basic.includes.3.label',
            detail: boneDensityDetail,
          },
          {
            label:
              'sections.diagnostics.plans.diagnostics-basic.includes.4.label',
            detail: ecgDetail,
          },
          {
            label:
              'sections.diagnostics.plans.diagnostics-basic.includes.5.label',
            detail: vitalsDetail,
          },
          {
            label:
              'sections.diagnostics.plans.diagnostics-basic.includes.6.label',
            detail: oralHealthDetail,
          },
          {
            label:
              'sections.diagnostics.plans.diagnostics-basic.includes.7.label',
            detail: cognitionDetail,
          },
          {
            label:
              'sections.diagnostics.plans.diagnostics-basic.includes.8.label',
            detail: gripStrengthDetail,
          },
          {
            label:
              'sections.diagnostics.plans.diagnostics-basic.includes.9.label',
            detail: nutritionDetail,
          },
          {
            label:
              'sections.diagnostics.plans.diagnostics-basic.includes.10.label',
            detail: doctorConsultationDetail,
          },
        ],
      },
      {
        id: 'diagnostics-pro',
        name: 'sections.diagnostics.plans.diagnostics-pro.name',
        tag: 'sections.diagnostics.plans.diagnostics-pro.tag',
        description: 'sections.diagnostics.plans.diagnostics-pro.description',
        price: 29999,
        buttonLabel: 'sections.diagnostics.plans.diagnostics-pro.buttonLabel',
        buttonVariant: 'filled',
        thumbnailUrl:
          'web_v2/performance-lab/pricing-page/placeholders/diagnostics/pro.png',
        shopifyVariantId:
          process.env['NEXT_PUBLIC_ENVIRONMENT'] === 'PRODUCTION'
            ? '44742040125510'
            : '55543513808969',
        includesHeader:
          'sections.diagnostics.plans.diagnostics-pro.includesHeader',
        includes: [
          {
            label:
              'sections.diagnostics.plans.diagnostics-pro.includes.8.label',
            detail: bloodVisionPeakDetail,
          },
          {
            label:
              'sections.diagnostics.plans.diagnostics-pro.includes.0.label',
            detail: tScoreZScoreDetail,
          },
          {
            label:
              'sections.diagnostics.plans.diagnostics-pro.includes.1.label',
            detail: vo2MaxTreadmillDetail,
          },
          {
            label:
              'sections.diagnostics.plans.diagnostics-pro.includes.2.label',
            detail: musculoskeletalProDetail,
          },
          {
            label:
              'sections.diagnostics.plans.diagnostics-pro.includes.3.label',
            detail: lungHealthDetail,
          },
          {
            label:
              'sections.diagnostics.plans.diagnostics-pro.includes.4.label',
            detail: hearingDetail,
          },
          {
            label:
              'sections.diagnostics.plans.diagnostics-pro.includes.5.label',
            detail: visionDetail,
          },
          {
            label:
              'sections.diagnostics.plans.diagnostics-pro.includes.6.label',
            detail: nutritionDetail,
          },
          {
            label:
              'sections.diagnostics.plans.diagnostics-pro.includes.7.label',
            detail: personalizedRoadmapDetail,
          },
        ],
        secondaryIncludesHeader:
          'sections.diagnostics.plans.diagnostics-pro.secondaryIncludesHeader',
        secondaryIncludes: [
          {
            label:
              'sections.diagnostics.plans.diagnostics-basic.includes.0.label',
            detail: visionDashboardDetail,
          },
          {
            label:
              'sections.diagnostics.plans.diagnostics-basic.includes.2.label',
            detail: bodyCompositionDetail,
          },
          {
            label:
              'sections.diagnostics.plans.diagnostics-basic.includes.3.label',
            detail: boneDensityDetail,
          },
          {
            label:
              'sections.diagnostics.plans.diagnostics-basic.includes.4.label',
            detail: ecgDetail,
          },
          {
            label:
              'sections.diagnostics.plans.diagnostics-basic.includes.5.label',
            detail: vitalsDetail,
          },
          {
            label:
              'sections.diagnostics.plans.diagnostics-basic.includes.6.label',
            detail: oralHealthDetail,
          },
          {
            label:
              'sections.diagnostics.plans.diagnostics-basic.includes.7.label',
            detail: cognitionDetail,
          },
          {
            label:
              'sections.diagnostics.plans.diagnostics-basic.includes.8.label',
            detail: gripStrengthDetail,
          },
          {
            label:
              'sections.diagnostics.plans.diagnostics-basic.includes.9.label',
            detail: nutritionDetail,
          },
          {
            label:
              'sections.diagnostics.plans.diagnostics-basic.includes.10.label',
            detail: doctorConsultationDetail,
          },
        ],
      },
      {
        id: 'diagnostics-elite',
        name: 'sections.diagnostics.plans.diagnostics-elite.name',
        description: 'sections.diagnostics.plans.diagnostics-elite.description',
        price: 69999,
        buttonLabel: 'sections.diagnostics.plans.diagnostics-elite.buttonLabel',
        buttonVariant: 'filled',
        thumbnailUrl:
          'web_v2/performance-lab/pricing-page/placeholders/diagnostics/elite.png',
        shopifyVariantId:
          process.env['NEXT_PUBLIC_ENVIRONMENT'] === 'PRODUCTION'
            ? '44742040158278'
            : '55543513808969',
        includesHeader:
          'sections.diagnostics.plans.diagnostics-elite.includesHeader',
        includes: [
          {
            label:
              'sections.diagnostics.plans.diagnostics-elite.includes.0.label',
            detail: nutriDNADetail,
          },
          {
            label:
              'sections.diagnostics.plans.diagnostics-elite.includes.1.label',
            detail: gutMicrobiomeDetail,
          },
        ],
        secondaryIncludesHeader:
          'sections.diagnostics.plans.diagnostics-elite.secondaryIncludesHeader',
        secondaryIncludes: [
          {
            label:
              'sections.diagnostics.plans.diagnostics-pro.includes.8.label',
            detail: bloodVisionPeakDetail,
          },
          {
            label:
              'sections.diagnostics.plans.diagnostics-pro.includes.0.label',
            detail: tScoreZScoreDetail,
          },
          {
            label:
              'sections.diagnostics.plans.diagnostics-pro.includes.1.label',
            detail: vo2MaxTreadmillDetail,
          },
          {
            label:
              'sections.diagnostics.plans.diagnostics-pro.includes.2.label',
            detail: musculoskeletalProDetail,
          },
          {
            label:
              'sections.diagnostics.plans.diagnostics-pro.includes.3.label',
            detail: lungHealthDetail,
          },
          {
            label:
              'sections.diagnostics.plans.diagnostics-pro.includes.4.label',
            detail: hearingDetail,
          },
          {
            label:
              'sections.diagnostics.plans.diagnostics-pro.includes.5.label',
            detail: visionDetail,
          },
          {
            label:
              'sections.diagnostics.plans.diagnostics-pro.includes.6.label',
            detail: nutritionDetail,
          },
          {
            label:
              'sections.diagnostics.plans.diagnostics-pro.includes.7.label',
            detail: personalizedRoadmapDetail,
          },
          {
            label:
              'sections.diagnostics.plans.diagnostics-basic.includes.0.label',
            detail: visionDashboardDetail,
          },
          {
            label:
              'sections.diagnostics.plans.diagnostics-basic.includes.2.label',
            detail: bodyCompositionDetail,
          },
          {
            label:
              'sections.diagnostics.plans.diagnostics-basic.includes.3.label',
            detail: boneDensityDetail,
          },
          {
            label:
              'sections.diagnostics.plans.diagnostics-basic.includes.4.label',
            detail: ecgDetail,
          },
          {
            label:
              'sections.diagnostics.plans.diagnostics-basic.includes.5.label',
            detail: vitalsDetail,
          },
          {
            label:
              'sections.diagnostics.plans.diagnostics-basic.includes.6.label',
            detail: oralHealthDetail,
          },
          {
            label:
              'sections.diagnostics.plans.diagnostics-basic.includes.7.label',
            detail: cognitionDetail,
          },
          {
            label:
              'sections.diagnostics.plans.diagnostics-basic.includes.8.label',
            detail: gripStrengthDetail,
          },
          {
            label:
              'sections.diagnostics.plans.diagnostics-basic.includes.9.label',
            detail: nutritionDetail,
          },
          {
            label:
              'sections.diagnostics.plans.diagnostics-basic.includes.10.label',
            detail: doctorConsultationDetail,
          },
        ],
      },
    ],
    compareData: [
      {
        name: 'sections.diagnostics.compareData.0.name',
        thumbnailUrl:
          'web_v2/performance-lab/pricing-page/placeholders/diagnostics/doctor-consultation.png',
        items: [
          {
            name: 'sections.diagnostics.compareData.0.items.0.name',
            columns: [true, true, true],
          },
        ],
      },
      {
        name: 'sections.diagnostics.compareData.1.name',
        thumbnailUrl:
          'web_v2/performance-lab/pricing-page/placeholders/diagnostics/blood-test.png',
        items: [
          {
            name: 'sections.diagnostics.compareData.1.items.0.name',
            columns: [
              'sections.diagnostics.compareData.1.items.0.columns.0',
              'sections.diagnostics.compareData.1.items.0.columns.1',
              'sections.diagnostics.compareData.1.items.0.columns.2',
            ],
          },
        ],
      },
      {
        name: 'sections.diagnostics.compareData.2.name',
        thumbnailUrl:
          'web_v2/performance-lab/pricing-page/placeholders/diagnostics/body-composition-analysis.png',
        items: [
          {
            name: 'sections.diagnostics.compareData.2.items.0.name',
            columns: [true, true, true],
          },
          {
            name: 'sections.diagnostics.compareData.2.items.1.name',
            columns: [true, true, true],
          },
          {
            name: 'sections.diagnostics.compareData.2.items.2.name',
            columns: [true, true, true],
          },
          {
            name: 'sections.diagnostics.compareData.2.items.3.name',
            columns: [true, true, true],
          },
          {
            name: 'sections.diagnostics.compareData.2.items.4.name',
            columns: [true, true, true],
          },
        ],
      },
      {
        name: 'sections.diagnostics.compareData.3.name',
        thumbnailUrl:
          'web_v2/performance-lab/pricing-page/placeholders/diagnostics/bone-density.png',
        items: [
          {
            name: 'sections.diagnostics.compareData.3.items.0.name',
            columns: [true, true, true],
          },
          {
            name: 'sections.diagnostics.compareData.3.items.1.name',
            columns: [false, true, true],
          },
        ],
      },
      {
        name: 'sections.diagnostics.compareData.4.name',
        thumbnailUrl:
          'web_v2/performance-lab/pricing-page/placeholders/diagnostics/ecg.png',
        items: [
          {
            name: 'sections.diagnostics.compareData.4.items.0.name',
            columns: [true, true, true],
          },
          {
            name: 'sections.diagnostics.compareData.4.items.1.name',
            columns: [true, true, true],
          },
        ],
      },
      {
        name: 'sections.diagnostics.compareData.5.name',
        thumbnailUrl:
          'web_v2/performance-lab/pricing-page/placeholders/diagnostics/vitals.png',
        items: [
          {
            name: 'sections.diagnostics.compareData.5.items.0.name',
            columns: [true, true, true],
          },
          {
            name: 'sections.diagnostics.compareData.5.items.1.name',
            columns: [true, true, true],
          },
          {
            name: 'sections.diagnostics.compareData.5.items.2.name',
            columns: [true, true, true],
          },
          {
            name: 'sections.diagnostics.compareData.5.items.3.name',
            columns: [true, true, true],
          },
          {
            name: 'sections.diagnostics.compareData.5.items.4.name',
            columns: [true, true, true],
          },
          {
            name: 'sections.diagnostics.compareData.5.items.5.name',
            columns: [true, true, true],
          },
          {
            name: 'sections.diagnostics.compareData.5.items.6.name',
            columns: [true, true, true],
          },
          {
            name: 'sections.diagnostics.compareData.5.items.7.name',
            columns: [true, true, true],
          },
        ],
      },
      {
        name: 'sections.diagnostics.compareData.6.name',
        thumbnailUrl:
          'web_v2/performance-lab/pricing-page/placeholders/diagnostics/oral-health.png',
        items: [
          {
            name: 'sections.diagnostics.compareData.6.items.0.name',
            columns: [true, true, true],
          },
          {
            name: 'sections.diagnostics.compareData.6.items.1.name',
            columns: [true, true, true],
          },
        ],
      },
      {
        name: 'sections.diagnostics.compareData.7.name',
        thumbnailUrl:
          'web_v2/performance-lab/pricing-page/placeholders/diagnostics/cognition.png',
        items: [
          {
            name: 'sections.diagnostics.compareData.7.items.0.name',
            columns: [true, true, true],
          },
          {
            name: 'sections.diagnostics.compareData.7.items.1.name',
            columns: [true, true, true],
          },
          {
            name: 'sections.diagnostics.compareData.7.items.2.name',
            columns: [true, true, true],
          },
          {
            name: 'sections.diagnostics.compareData.7.items.3.name',
            columns: [true, true, true],
          },
          {
            name: 'sections.diagnostics.compareData.7.items.4.name',
            columns: [true, true, true],
          },
        ],
      },
      {
        name: 'sections.diagnostics.compareData.8.name',
        thumbnailUrl:
          'web_v2/performance-lab/pricing-page/placeholders/diagnostics/vo2-max.png',
        items: [
          {
            name: 'sections.diagnostics.compareData.8.items.0.name',
            columns: [false, true, true],
          },
          {
            name: 'sections.diagnostics.compareData.8.items.1.name',
            columns: [false, true, true],
          },
          {
            name: 'sections.diagnostics.compareData.8.items.2.name',
            columns: [false, true, true],
          },
          {
            name: 'sections.diagnostics.compareData.8.items.3.name',
            columns: [false, true, true],
          },
          {
            name: 'sections.diagnostics.compareData.8.items.4.name',
            columns: [false, true, true],
          },
        ],
      },
      {
        name: 'sections.diagnostics.compareData.9.name',
        thumbnailUrl:
          'web_v2/performance-lab/pricing-page/placeholders/diagnostics/musculoskeletal-assessment.png',
        items: [
          {
            name: 'sections.diagnostics.compareData.9.items.0.name',
            columns: [true, true, true],
          },
          {
            name: 'sections.diagnostics.compareData.9.items.1.name',
            columns: [false, true, true],
          },
          {
            name: 'sections.diagnostics.compareData.9.items.2.name',
            columns: [false, true, true],
          },
          {
            name: 'sections.diagnostics.compareData.9.items.3.name',
            columns: [false, true, true],
          },
          {
            name: 'sections.diagnostics.compareData.9.items.4.name',
            columns: [false, true, true],
          },
        ],
      },
      {
        name: 'sections.diagnostics.compareData.10.name',
        thumbnailUrl:
          'web_v2/performance-lab/pricing-page/placeholders/diagnostics/pulmonary-function-test.png',
        items: [
          {
            name: 'sections.diagnostics.compareData.10.items.0.name',
            columns: [false, true, true],
          },
          {
            name: 'sections.diagnostics.compareData.10.items.1.name',
            columns: [false, true, true],
          },
        ],
      },
      {
        name: 'sections.diagnostics.compareData.11.name',
        thumbnailUrl:
          'web_v2/performance-lab/pricing-page/placeholders/diagnostics/hearing.png',
        items: [
          {
            name: 'sections.diagnostics.compareData.11.items.0.name',
            columns: [false, true, true],
          },
          {
            name: 'sections.diagnostics.compareData.11.items.1.name',
            columns: [false, true, true],
          },
        ],
      },
      {
        name: 'sections.diagnostics.compareData.12.name',
        thumbnailUrl:
          'web_v2/performance-lab/pricing-page/placeholders/diagnostics/vision.png',
        items: [
          {
            name: 'sections.diagnostics.compareData.12.items.0.name',
            columns: [false, true, true],
          },
          {
            name: 'sections.diagnostics.compareData.12.items.1.name',
            columns: [false, true, true],
          },
          {
            name: 'sections.diagnostics.compareData.12.items.2.name',
            columns: [false, true, true],
          },
          {
            name: 'sections.diagnostics.compareData.12.items.3.name',
            columns: [false, true, true],
          },
          {
            name: 'sections.diagnostics.compareData.12.items.4.name',
            columns: [false, true, true],
          },
        ],
      },
      {
        name: 'sections.diagnostics.compareData.13.name',
        thumbnailUrl:
          'web_v2/performance-lab/pricing-page/placeholders/diagnostics/nutrition.png',
        items: [
          {
            name: 'sections.diagnostics.compareData.13.items.0.name',
            columns: [true, true, true],
          },
          {
            name: 'sections.diagnostics.compareData.13.items.1.name',
            columns: [true, true, true],
          },
          {
            name: 'sections.diagnostics.compareData.13.items.2.name',
            columns: [true, true, true],
          },
          {
            name: 'sections.diagnostics.compareData.13.items.3.name',
            columns: [false, true, true],
          },
          {
            name: 'sections.diagnostics.compareData.13.items.4.name',
            columns: [false, true, true],
          },
        ],
      },
      {
        name: 'sections.diagnostics.compareData.14.name',
        thumbnailUrl:
          'web_v2/performance-lab/pricing-page/placeholders/diagnostics/doctor-consultation.png',
        items: [
          {
            name: 'sections.diagnostics.compareData.14.items.0.name',
            columns: [true, true, true],
          },
          {
            name: 'sections.diagnostics.compareData.14.items.1.name',
            columns: [
              'sections.diagnostics.compareData.14.items.1.columns.0',
              'sections.diagnostics.compareData.14.items.1.columns.1',
              'sections.diagnostics.compareData.14.items.1.columns.2',
            ],
          },
          {
            name: 'sections.diagnostics.compareData.14.items.2.name',
            columns: [true, true, true],
          },
        ],
      },
      {
        name: 'sections.diagnostics.compareData.15.name',
        thumbnailUrl:
          'web_v2/performance-lab/pricing-page/placeholders/diagnostics/genetic-testing.png',
        items: [
          {
            name: 'sections.diagnostics.compareData.15.items.0.name',
            columns: [false, false, true],
          },
          {
            name: 'sections.diagnostics.compareData.15.items.1.name',
            columns: [false, false, true],
          },
          {
            name: 'sections.diagnostics.compareData.15.items.2.name',
            columns: [false, false, true],
          },
          {
            name: 'sections.diagnostics.compareData.15.items.3.name',
            columns: [false, false, true],
          },
          {
            name: 'sections.diagnostics.compareData.15.items.4.name',
            columns: [false, false, true],
          },
          {
            name: 'sections.diagnostics.compareData.15.items.5.name',
            columns: [false, false, true],
          },
          {
            name: 'sections.diagnostics.compareData.15.items.6.name',
            columns: [false, false, true],
          },
          {
            name: 'sections.diagnostics.compareData.15.items.7.name',
            columns: [false, false, true],
          },
          {
            name: 'sections.diagnostics.compareData.15.items.8.name',
            columns: [false, false, true],
          },
          {
            name: 'sections.diagnostics.compareData.15.items.9.name',
            columns: [false, false, true],
          },
          {
            name: 'sections.diagnostics.compareData.15.items.10.name',
            columns: [false, false, true],
          },
          {
            name: 'sections.diagnostics.compareData.15.items.11.name',
            columns: [false, false, true],
          },
          {
            name: 'sections.diagnostics.compareData.15.items.12.name',
            columns: [false, false, true],
          },
          {
            name: 'sections.diagnostics.compareData.15.items.13.name',
            columns: [false, false, true],
          },
          {
            name: 'sections.diagnostics.compareData.15.items.14.name',
            columns: [false, false, true],
          },
        ],
      },
      {
        name: 'sections.diagnostics.compareData.16.name',
        thumbnailUrl:
          'web_v2/performance-lab/pricing-page/placeholders/diagnostics/gut-microbiome.png',
        items: [
          {
            name: 'sections.diagnostics.compareData.16.items.0.name',
            columns: [false, false, true],
          },
          {
            name: 'sections.diagnostics.compareData.16.items.1.name',
            columns: [false, false, true],
          },
          {
            name: 'sections.diagnostics.compareData.16.items.2.name',
            columns: [false, false, true],
          },
          {
            name: 'sections.diagnostics.compareData.16.items.3.name',
            columns: [false, false, true],
          },
          {
            name: 'sections.diagnostics.compareData.16.items.4.name',
            columns: [false, false, true],
          },
          {
            name: 'sections.diagnostics.compareData.16.items.5.name',
            columns: [false, false, true],
          },
          {
            name: 'sections.diagnostics.compareData.16.items.6.name',
            columns: [false, false, true],
          },
          {
            name: 'sections.diagnostics.compareData.16.items.7.name',
            columns: [false, false, true],
          },
          {
            name: 'sections.diagnostics.compareData.16.items.8.name',
            columns: [false, false, true],
          },
          {
            name: 'sections.diagnostics.compareData.16.items.9.name',
            columns: [false, false, true],
          },
          {
            name: 'sections.diagnostics.compareData.16.items.10.name',
            columns: [false, false, true],
          },
        ],
      },
    ],
  },

  // ─── 2. REPEAT RECOVERY ────────────────────────────────────────────
  {
    id: 'recovery',
    sidebarLabel: 'sections.recovery.sidebarLabel',
    heroTitle: 'sections.recovery.heroTitle',
    heroImageUrl:
      'web_v2/performance-lab/pricing-page/placeholders/heroes/recovery-1.png',
    heroDescription: 'sections.recovery.heroDescription',
    heroTextPosition: 'right',
    cardLayout: '3-col',
    plans: [
      {
        id: 'recovery-day-pass',
        name: 'sections.recovery.plans.recovery-day-pass.name',
        thumbnailUrl:
          'web_v2/performance-lab/pricing-page/placeholders/recovery/day-pass-1.png',
        description: 'sections.recovery.plans.recovery-day-pass.description',
        sessionInfo: 'sections.recovery.plans.recovery-day-pass.sessionInfo',
        validity: 'sections.recovery.plans.recovery-day-pass.validity',
        price: 1999,
        buttonLabel: 'sections.recovery.plans.recovery-day-pass.buttonLabel',
        buttonVariant: 'filled',
        shopifyVariantId:
          process.env['NEXT_PUBLIC_ENVIRONMENT'] === 'PRODUCTION'
            ? '44742040322118'
            : '55543515545673',
        includesHeader:
          'sections.recovery.plans.recovery-day-pass.includesHeader',
        includes: [
          {
            label: 'sections.recovery.plans.recovery-day-pass.includes.0.label',
            detail: recoveryPowerplateDetail,
          },
          {
            label: 'sections.recovery.plans.recovery-day-pass.includes.1.label',
            detail: recoveryLocalizedHotColdDetail,
          },
          {
            label: 'sections.recovery.plans.recovery-day-pass.includes.2.label',
            detail: recoveryCompressionDetail,
          },
          {
            label: 'sections.recovery.plans.recovery-day-pass.includes.3.label',
            detail: recoveryDecompressionDetail,
          },
          {
            label: 'sections.recovery.plans.recovery-day-pass.includes.4.label',
            detail: recoveryMassageGunsDetail,
          },
          {
            label: 'sections.recovery.plans.recovery-day-pass.includes.5.label',
            detail: recoveryColdPlungeDetail,
          },
          {
            label: 'sections.recovery.plans.recovery-day-pass.includes.6.label',
            detail: recoveryInfraredSaunaDetail,
          },
        ],
      },
      {
        id: 'recovery-month-pass',
        name: 'sections.recovery.plans.recovery-month-pass.name',
        thumbnailUrl:
          'web_v2/performance-lab/pricing-page/placeholders/recovery/month-pass-1.png',
        description: 'sections.recovery.plans.recovery-month-pass.description',
        sessionInfo: 'sections.recovery.plans.recovery-month-pass.sessionInfo',
        validity: 'sections.recovery.plans.recovery-month-pass.validity',
        price: 9999,
        buttonLabel: 'sections.recovery.plans.recovery-month-pass.buttonLabel',
        buttonVariant: 'filled',
        tag: 'sections.recovery.plans.recovery-month-pass.tag',
        shopifyVariantId:
          process.env['NEXT_PUBLIC_ENVIRONMENT'] === 'PRODUCTION'
            ? '44742040387654'
            : '55543515545673',
        includesHeader:
          'sections.recovery.plans.recovery-month-pass.includesHeader',
        includes: [
          {
            label:
              'sections.recovery.plans.recovery-month-pass.includes.0.label',
            detail: recoveryPowerplateDetail,
          },
          {
            label:
              'sections.recovery.plans.recovery-month-pass.includes.1.label',
            detail: recoveryLocalizedHotColdDetail,
          },
          {
            label:
              'sections.recovery.plans.recovery-month-pass.includes.2.label',
            detail: recoveryCompressionDetail,
          },
          {
            label:
              'sections.recovery.plans.recovery-month-pass.includes.3.label',
            detail: recoveryDecompressionDetail,
          },
          {
            label:
              'sections.recovery.plans.recovery-month-pass.includes.4.label',
            detail: recoveryMassageGunsDetail,
          },
          {
            label:
              'sections.recovery.plans.recovery-month-pass.includes.5.label',
            detail: recoveryColdPlungeDetail,
          },
          {
            label:
              'sections.recovery.plans.recovery-month-pass.includes.6.label',
            detail: recoveryInfraredSaunaDetail,
          },
        ],
      },
      {
        id: 'recovery-3-month-pass',
        name: 'sections.recovery.plans.recovery-3-month-pass.name',
        thumbnailUrl:
          'web_v2/performance-lab/pricing-page/placeholders/recovery/3-month-pass-1.png',
        description:
          'sections.recovery.plans.recovery-3-month-pass.description',
        sessionInfo:
          'sections.recovery.plans.recovery-3-month-pass.sessionInfo',
        validity: 'sections.recovery.plans.recovery-3-month-pass.validity',
        price: 15999,
        buttonLabel:
          'sections.recovery.plans.recovery-3-month-pass.buttonLabel',
        buttonVariant: 'filled',
        shopifyVariantId:
          process.env['NEXT_PUBLIC_ENVIRONMENT'] === 'PRODUCTION'
            ? '44742040420422'
            : '55543515545673',
        includesHeader:
          'sections.recovery.plans.recovery-3-month-pass.includesHeader',
        includes: [
          {
            label:
              'sections.recovery.plans.recovery-3-month-pass.includes.0.label',
            detail: recoveryPowerplateDetail,
          },
          {
            label:
              'sections.recovery.plans.recovery-3-month-pass.includes.1.label',
            detail: recoveryLocalizedHotColdDetail,
          },
          {
            label:
              'sections.recovery.plans.recovery-3-month-pass.includes.2.label',
            detail: recoveryCompressionDetail,
          },
          {
            label:
              'sections.recovery.plans.recovery-3-month-pass.includes.3.label',
            detail: recoveryDecompressionDetail,
          },
          {
            label:
              'sections.recovery.plans.recovery-3-month-pass.includes.4.label',
            detail: recoveryMassageGunsDetail,
          },
          {
            label:
              'sections.recovery.plans.recovery-3-month-pass.includes.5.label',
            detail: recoveryColdPlungeDetail,
          },
          {
            label:
              'sections.recovery.plans.recovery-3-month-pass.includes.6.label',
            detail: recoveryInfraredSaunaDetail,
          },
        ],
      },
    ],
    compareHeaders: [
      'sections.recovery.compareHeaders.0',
      'sections.recovery.compareHeaders.1',
      'sections.recovery.compareHeaders.2',
    ],
    compareData: [
      {
        name: 'sections.recovery.compareData.0.name',
        thumbnailUrl:
          'web_v2/performance-lab/pricing-page/placeholders/diagnostics/recovery-services.png',
        items: [
          {
            name: 'sections.recovery.compareData.0.items.0.name',
            columns: [
              'sections.recovery.compareData.0.items.0.columns.0',
              'sections.recovery.compareData.0.items.0.columns.1',
              'sections.recovery.compareData.0.items.0.columns.2',
            ],
          },
          {
            name: 'sections.recovery.compareData.0.items.1.name',
            columns: [
              'sections.recovery.compareData.0.items.1.columns.0',
              'sections.recovery.compareData.0.items.1.columns.1',
              'sections.recovery.compareData.0.items.1.columns.2',
            ],
          },
          {
            name: 'sections.recovery.compareData.0.items.2.name',
            columns: [
              'sections.recovery.compareData.0.items.2.columns.0',
              'sections.recovery.compareData.0.items.2.columns.1',
              'sections.recovery.compareData.0.items.2.columns.2',
            ],
          },
          {
            name: 'sections.recovery.compareData.0.items.3.name',
            columns: [true, true, true],
          },
          {
            name: 'sections.recovery.compareData.0.items.4.name',
            columns: [true, true, true],
          },
          {
            name: 'sections.recovery.compareData.0.items.5.name',
            columns: [true, true, true],
          },
          {
            name: 'sections.recovery.compareData.0.items.6.name',
            columns: [true, true, true],
          },
          {
            name: 'sections.recovery.compareData.0.items.7.name',
            columns: [true, true, true],
          },
          {
            name: 'sections.recovery.compareData.0.items.8.name',
            columns: [true, true, true],
          },
          {
            name: 'sections.recovery.compareData.0.items.9.name',
            columns: [true, true, true],
          },
        ],
      },
    ],
  },

  // ─── 3. STANDALONE MODULES ─────────────────────────────────────────
  {
    id: 'standalone',
    sidebarLabel: 'sections.standalone.sidebarLabel',
    heroTitle: 'sections.standalone.heroTitle',
    heroImageUrl:
      'web_v2/performance-lab/pricing-page/placeholders/heroes/standalone-1.png',
    heroDescription: 'sections.standalone.heroDescription',
    heroTextPosition: 'left',
    cardLayout: '2-col-wrap',
    cardVariant: 'compact',
    plans: [],
    subcategories: [
      {
        id: 'Therapies',
        label: 'sections.standalone.subcategories.therapy.label',
        plans: [
          {
            id: 'pl-standalone-hbot',
            name: 'sections.standalone.subcategories.therapy.plans.pl-standalone-hbot.name',
            description:
              'sections.standalone.subcategories.therapy.plans.pl-standalone-hbot.description',
            price: 9999,
            buttonLabel:
              'sections.standalone.subcategories.therapy.plans.pl-standalone-hbot.buttonLabel',
            buttonVariant: 'filled',
            learnMore: true,
            thumbnailUrl:
              'web_v2/performance-lab/pricing-page/placeholders/standalone/hyperbaric-oxygen.png',
            detail: recoveryHBOTDetail,
            shopifyVariantId:
              process.env['NEXT_PUBLIC_ENVIRONMENT'] === 'PRODUCTION'
                ? '44742040453190'
                : '55543518363721',
          },
          {
            id: 'pl-standalone-massage-male',
            name: 'sections.standalone.subcategories.therapy.plans.pl-standalone-massage-male.name',
            description:
              'sections.standalone.subcategories.therapy.plans.pl-standalone-massage-male.description',
            price: 2499,
            buttonLabel:
              'sections.standalone.subcategories.therapy.plans.pl-standalone-massage-male.buttonLabel',
            buttonVariant: 'filled',
            learnMore: true,
            thumbnailUrl:
              'web_v2/performance-lab/pricing-page/placeholders/standalone/sports-massage.png',
            detail: recoverySportsMassageDetail,
            shopifyVariantId:
              process.env['NEXT_PUBLIC_ENVIRONMENT'] === 'PRODUCTION'
                ? '44742040518726'
                : '55543518363721',
          },
          {
            id: 'pl-standalone-massage-female',
            name: 'sections.standalone.subcategories.therapy.plans.pl-standalone-massage-female.name',
            description:
              'sections.standalone.subcategories.therapy.plans.pl-standalone-massage-female.description',
            price: 2499,
            buttonLabel:
              'sections.standalone.subcategories.therapy.plans.pl-standalone-massage-female.buttonLabel',
            buttonVariant: 'filled',
            learnMore: true,
            thumbnailUrl:
              'web_v2/performance-lab/pricing-page/placeholders/standalone/sports-massage.png',
            detail: recoverySportsMassageDetail,
            shopifyVariantId:
              process.env['NEXT_PUBLIC_ENVIRONMENT'] === 'PRODUCTION'
                ? '44813857456198'
                : '55543518363721',
          },
          {
            id: 'pl-standalone-cryo',
            name: 'sections.standalone.subcategories.therapy.plans.pl-standalone-cryo.name',
            description:
              'sections.standalone.subcategories.therapy.plans.pl-standalone-cryo.description',
            price: 3999,
            buttonLabel:
              'sections.standalone.subcategories.therapy.plans.pl-standalone-cryo.buttonLabel',
            buttonVariant: 'filled',
            learnMore: true,
            thumbnailUrl:
              'web_v2/performance-lab/pricing-page/placeholders/standalone/cryotherapy.png',
            detail: recoveryCryotherapyDetail,
            shopifyVariantId:
              process.env['NEXT_PUBLIC_ENVIRONMENT'] === 'PRODUCTION'
                ? '44742040584262'
                : '55543518363721',
          },
        ],
      },
      {
        id: 'Diagnostics',
        label: 'sections.standalone.subcategories.diagnostic.label',
        plans: [
          {
            id: 'pl-addon-vo2max-treadmill',
            name: 'sections.standalone.subcategories.diagnostic.plans.pl-addon-vo2max-treadmill.name',
            description:
              'sections.standalone.subcategories.diagnostic.plans.pl-addon-vo2max-treadmill.description',
            price: 7499,
            buttonLabel:
              'sections.standalone.subcategories.diagnostic.plans.pl-addon-vo2max-treadmill.buttonLabel',
            buttonVariant: 'filled',
            learnMore: true,
            thumbnailUrl:
              'web_v2/performance-lab/pricing-page/placeholders/standalone/vo2-max-treadmill.png',
            detail: vo2MaxTreadmillDetail,
            shopifyVariantId:
              process.env['NEXT_PUBLIC_ENVIRONMENT'] === 'PRODUCTION'
                ? '44742043435078'
                : '55543518363721',
          },
          {
            id: 'pl-addon-vo2max-bike',
            name: 'sections.standalone.subcategories.diagnostic.plans.pl-addon-vo2max-bike.name',
            description:
              'sections.standalone.subcategories.diagnostic.plans.pl-addon-vo2max-bike.description',
            price: 7499,
            buttonLabel:
              'sections.standalone.subcategories.diagnostic.plans.pl-addon-vo2max-bike.buttonLabel',
            buttonVariant: 'filled',
            learnMore: true,
            thumbnailUrl:
              'web_v2/performance-lab/pricing-page/placeholders/standalone/vo2-max-bike.png',
            detail: vo2MaxBikeDetail,
            shopifyVariantId:
              process.env['NEXT_PUBLIC_ENVIRONMENT'] === 'PRODUCTION'
                ? '44742043369542'
                : '55543518363721',
          },
          {
            id: 'pl-addon-dexa',
            name: 'sections.standalone.subcategories.diagnostic.plans.pl-addon-dexa.name',
            description:
              'sections.standalone.subcategories.diagnostic.plans.pl-addon-dexa.description',
            price: 3499,
            buttonLabel:
              'sections.standalone.subcategories.diagnostic.plans.pl-addon-dexa.buttonLabel',
            buttonVariant: 'filled',
            learnMore: true,
            thumbnailUrl:
              'web_v2/performance-lab/pricing-page/placeholders/standalone/dexa.png',
            detail: bodyCompositionDetail,
            shopifyVariantId:
              process.env['NEXT_PUBLIC_ENVIRONMENT'] === 'PRODUCTION'
                ? '44742043467846'
                : '55543518363721',
          },
          {
            id: 'pl-addon-genome-health',
            name: 'sections.standalone.subcategories.diagnostic.plans.pl-addon-genome-health.name',
            description:
              'sections.standalone.subcategories.diagnostic.plans.pl-addon-genome-health.description',
            price: 35999,
            buttonLabel:
              'sections.standalone.subcategories.diagnostic.plans.pl-addon-genome-health.buttonLabel',
            buttonVariant: 'filled',
            learnMore: true,
            thumbnailUrl:
              'web_v2/performance-lab/pricing-page/placeholders/standalone/genomic-health-insights.png',
            detail: genomicHealthDetail,
            // Shopify product: Genetic testing (8401622564934)
            // Variant ID pending — publish product to Storefront/Headless channel, then set here.
            shopifyVariantId:
              process.env['NEXT_PUBLIC_ENVIRONMENT'] === 'PRODUCTION'
                ? '45060456775750'
                : '55543518363721',
            // bulletPoints: [
            //   'sections.standalone.subcategories.diagnostic.plans.pl-addon-genome-health.bulletPoints.0',
            //   'sections.standalone.subcategories.diagnostic.plans.pl-addon-genome-health.bulletPoints.1',
            //   'sections.standalone.subcategories.diagnostic.plans.pl-addon-genome-health.bulletPoints.2',
            // ],
          },
          {
            id: 'pl-addon-gut-microbiome',
            name: 'sections.standalone.subcategories.diagnostic.plans.pl-addon-gut-microbiome.name',
            description:
              'sections.standalone.subcategories.diagnostic.plans.pl-addon-gut-microbiome.description',
            price: 7999,
            buttonLabel:
              'sections.standalone.subcategories.diagnostic.plans.pl-addon-gut-microbiome.buttonLabel',
            buttonVariant: 'filled',
            learnMore: true,
            thumbnailUrl:
              'web_v2/performance-lab/pricing-page/placeholders/standalone/gut-microbiome.png',
            detail: gutMicrobiomeDetail,
            shopifyVariantId:
              process.env['NEXT_PUBLIC_ENVIRONMENT'] === 'PRODUCTION'
                ? '44742043336774'
                : '55543518363721',
            // bulletPoints: [
            //   'sections.standalone.subcategories.diagnostic.plans.pl-addon-gut-microbiome.bulletPoints.0',
            //   'sections.standalone.subcategories.diagnostic.plans.pl-addon-gut-microbiome.bulletPoints.1',
            // ],
          },
          {
            id: 'pl-addon-nutrition-pro',
            name: 'sections.standalone.subcategories.diagnostic.plans.pl-addon-nutrition-pro.name',
            description:
              'sections.standalone.subcategories.diagnostic.plans.pl-addon-nutrition-pro.description',
            price: 24999,
            buttonLabel:
              'sections.standalone.subcategories.diagnostic.plans.pl-addon-nutrition-pro.buttonLabel',
            buttonVariant: 'filled',
            learnMore: true,
            thumbnailUrl:
              'web_v2/performance-lab/pricing-page/placeholders/standalone/nutrition-pro.png',
            detail: nutritionDetail,
            shopifyVariantId:
              process.env['NEXT_PUBLIC_ENVIRONMENT'] === 'PRODUCTION'
                ? '44742043533382'
                : '55543518363721',
            // bulletPoints: [
            //   'sections.standalone.subcategories.diagnostic.plans.pl-addon-nutrition-pro.bulletPoints.0',
            //   'sections.standalone.subcategories.diagnostic.plans.pl-addon-nutrition-pro.bulletPoints.1',
            //   'sections.standalone.subcategories.diagnostic.plans.pl-addon-nutrition-pro.bulletPoints.2',
            // ],
          },
          {
            id: 'pl-addon-eye-scan',
            name: 'sections.standalone.subcategories.diagnostic.plans.pl-addon-eye-scan.name',
            description:
              'sections.standalone.subcategories.diagnostic.plans.pl-addon-eye-scan.description',
            price: 3499,
            buttonLabel:
              'sections.standalone.subcategories.diagnostic.plans.pl-addon-eye-scan.buttonLabel',
            buttonVariant: 'filled',
            learnMore: true,
            thumbnailUrl:
              'web_v2/performance-lab/pricing-page/placeholders/standalone/eye-scan.png',
            detail: visionDetail,
            shopifyVariantId:
              process.env['NEXT_PUBLIC_ENVIRONMENT'] === 'PRODUCTION'
                ? '44742043500614'
                : '55543518363721',
            bulletPoints: [],
          },
        ],
      },
    ],
  },

  // ─── 4. IVs & IMs ──────────────────────────────────────────────────
  {
    id: 'iv-therapy',
    sidebarLabel: 'sections.iv-therapy.sidebarLabel',
    heroTitle: 'sections.iv-therapy.heroTitle',
    heroImageUrl:
      'web_v2/performance-lab/pricing-page/placeholders/heroes/iv-therapy-1.png',
    heroDescription: 'sections.iv-therapy.heroDescription',
    heroTextPosition: 'left',
    cardLayout: '2-col-wrap',
    cardVariant: 'compact',
    plans: [],
    subcategories: [
      {
        id: 'iv',
        label: 'sections.iv-therapy.subcategories.iv.label',
        plans: [
          {
            id: 'iv-miniboost',
            name: 'sections.iv-therapy.subcategories.iv.plans.iv-miniboost.name',
            description:
              'sections.iv-therapy.subcategories.iv.plans.iv-miniboost.description',
            price: 7900,
            buttonLabel:
              'sections.iv-therapy.subcategories.iv.plans.iv-miniboost.buttonLabel',
            buttonVariant: 'filled',
            shopifyVariantId:
              process.env['NEXT_PUBLIC_ENVIRONMENT'] === 'PRODUCTION'
                ? '44742040617030'
                : '55543517184073',
            learnMore: true,
            detail: {
              description:
                'sections.iv-therapy.subcategories.iv.plans.iv-miniboost.detail.description',
              imageUrl:
                'web_v2/performance-lab/pricing-page/placeholders/sidebar/miniboost.png',
              sections: [
                {
                  title:
                    'sections.iv-therapy.subcategories.iv.plans.iv-miniboost.detail.sections.0.title',
                  content:
                    'sections.iv-therapy.subcategories.iv.plans.iv-miniboost.detail.sections.0.content',
                },
                {
                  title:
                    'sections.iv-therapy.subcategories.iv.plans.iv-miniboost.detail.sections.1.title',
                  content:
                    'sections.iv-therapy.subcategories.iv.plans.iv-miniboost.detail.sections.1.content',
                },
                {
                  title:
                    'sections.iv-therapy.subcategories.iv.plans.iv-miniboost.detail.sections.2.title',
                  content:
                    'sections.iv-therapy.subcategories.iv.plans.iv-miniboost.detail.sections.2.content',
                },
              ],
            },
            thumbnailUrl:
              'web_v2/performance-lab/pricing-page/placeholders/iv/miniboost.png',
            // bulletPoints: [
            //   'sections.iv-therapy.subcategories.iv.plans.iv-miniboost.bulletPoints.0',
            //   'sections.iv-therapy.subcategories.iv.plans.iv-miniboost.bulletPoints.1',
            //   'sections.iv-therapy.subcategories.iv.plans.iv-miniboost.bulletPoints.2',
            //   'sections.iv-therapy.subcategories.iv.plans.iv-miniboost.bulletPoints.3',
            // ],
          },
          {
            id: 'iv-hydromax',
            name: 'sections.iv-therapy.subcategories.iv.plans.iv-hydromax.name',
            description:
              'sections.iv-therapy.subcategories.iv.plans.iv-hydromax.description',
            price: 9900,
            buttonLabel:
              'sections.iv-therapy.subcategories.iv.plans.iv-hydromax.buttonLabel',
            buttonVariant: 'filled',
            shopifyVariantId:
              process.env['NEXT_PUBLIC_ENVIRONMENT'] === 'PRODUCTION'
                ? '44742040649798'
                : '55543517184073',
            learnMore: true,
            detail: {
              description:
                'sections.iv-therapy.subcategories.iv.plans.iv-hydromax.detail.description',
              imageUrl:
                'web_v2/performance-lab/pricing-page/placeholders/sidebar/hydromax.png',
              sections: [
                {
                  title:
                    'sections.iv-therapy.subcategories.iv.plans.iv-hydromax.detail.sections.0.title',
                  content:
                    'sections.iv-therapy.subcategories.iv.plans.iv-hydromax.detail.sections.0.content',
                },
                {
                  title:
                    'sections.iv-therapy.subcategories.iv.plans.iv-hydromax.detail.sections.1.title',
                  content:
                    'sections.iv-therapy.subcategories.iv.plans.iv-hydromax.detail.sections.1.content',
                },
                {
                  title:
                    'sections.iv-therapy.subcategories.iv.plans.iv-hydromax.detail.sections.2.title',
                  content:
                    'sections.iv-therapy.subcategories.iv.plans.iv-hydromax.detail.sections.2.content',
                },
              ],
            },
            thumbnailUrl:
              'web_v2/performance-lab/pricing-page/placeholders/iv/hydromax.png',
            // bulletPoints: [
            //   'sections.iv-therapy.subcategories.iv.plans.iv-hydromax.bulletPoints.0',
            //   'sections.iv-therapy.subcategories.iv.plans.iv-hydromax.bulletPoints.1',
            //   'sections.iv-therapy.subcategories.iv.plans.iv-hydromax.bulletPoints.2',
            //   'sections.iv-therapy.subcategories.iv.plans.iv-hydromax.bulletPoints.3',
            // ],
          },
          {
            id: 'iv-ultraviv',
            name: 'sections.iv-therapy.subcategories.iv.plans.iv-ultraviv.name',
            description:
              'sections.iv-therapy.subcategories.iv.plans.iv-ultraviv.description',
            price: 12900,
            buttonLabel:
              'sections.iv-therapy.subcategories.iv.plans.iv-ultraviv.buttonLabel',
            buttonVariant: 'filled',
            shopifyVariantId:
              process.env['NEXT_PUBLIC_ENVIRONMENT'] === 'PRODUCTION'
                ? '44742040682566'
                : '55543517184073',
            learnMore: true,
            detail: {
              description:
                'sections.iv-therapy.subcategories.iv.plans.iv-ultraviv.detail.description',
              imageUrl:
                'web_v2/performance-lab/pricing-page/placeholders/sidebar/ultraviv.png',
              sections: [
                {
                  title:
                    'sections.iv-therapy.subcategories.iv.plans.iv-ultraviv.detail.sections.0.title',
                  content:
                    'sections.iv-therapy.subcategories.iv.plans.iv-ultraviv.detail.sections.0.content',
                },
                {
                  title:
                    'sections.iv-therapy.subcategories.iv.plans.iv-ultraviv.detail.sections.1.title',
                  content:
                    'sections.iv-therapy.subcategories.iv.plans.iv-ultraviv.detail.sections.1.content',
                },
                {
                  title:
                    'sections.iv-therapy.subcategories.iv.plans.iv-ultraviv.detail.sections.2.title',
                  content:
                    'sections.iv-therapy.subcategories.iv.plans.iv-ultraviv.detail.sections.2.content',
                },
              ],
            },
            thumbnailUrl:
              'web_v2/performance-lab/pricing-page/placeholders/iv/ultraviv.png',
            // bulletPoints: [
            //   'sections.iv-therapy.subcategories.iv.plans.iv-ultraviv.bulletPoints.0',
            //   'sections.iv-therapy.subcategories.iv.plans.iv-ultraviv.bulletPoints.1',
            //   'sections.iv-therapy.subcategories.iv.plans.iv-ultraviv.bulletPoints.2',
            // ],
          },
          {
            id: 'iv-10x-health',
            name: 'sections.iv-therapy.subcategories.iv.plans.iv-10x-health.name',
            description:
              'sections.iv-therapy.subcategories.iv.plans.iv-10x-health.description',
            price: 13900,
            buttonLabel:
              'sections.iv-therapy.subcategories.iv.plans.iv-10x-health.buttonLabel',
            buttonVariant: 'filled',
            shopifyVariantId:
              process.env['NEXT_PUBLIC_ENVIRONMENT'] === 'PRODUCTION'
                ? '44742040715334'
                : '55543517184073',
            learnMore: true,
            detail: {
              description:
                'sections.iv-therapy.subcategories.iv.plans.iv-10x-health.detail.description',
              imageUrl:
                'web_v2/performance-lab/pricing-page/placeholders/sidebar/the-signature -10x.png',
              sections: [
                {
                  title:
                    'sections.iv-therapy.subcategories.iv.plans.iv-10x-health.detail.sections.0.title',
                  content:
                    'sections.iv-therapy.subcategories.iv.plans.iv-10x-health.detail.sections.0.content',
                },
                {
                  title:
                    'sections.iv-therapy.subcategories.iv.plans.iv-10x-health.detail.sections.1.title',
                  content:
                    'sections.iv-therapy.subcategories.iv.plans.iv-10x-health.detail.sections.1.content',
                },
                {
                  title:
                    'sections.iv-therapy.subcategories.iv.plans.iv-10x-health.detail.sections.2.title',
                  content:
                    'sections.iv-therapy.subcategories.iv.plans.iv-10x-health.detail.sections.2.content',
                },
              ],
            },
            thumbnailUrl:
              'web_v2/performance-lab/pricing-page/placeholders/iv/signature-10x.png',
            // bulletPoints: [
            //   'sections.iv-therapy.subcategories.iv.plans.iv-10x-health.bulletPoints.0',
            //   'sections.iv-therapy.subcategories.iv.plans.iv-10x-health.bulletPoints.1',
            //   'sections.iv-therapy.subcategories.iv.plans.iv-10x-health.bulletPoints.2',
            //   'sections.iv-therapy.subcategories.iv.plans.iv-10x-health.bulletPoints.3',
            // ],
          },
          {
            id: 'iv-megaboost',
            name: 'sections.iv-therapy.subcategories.iv.plans.iv-megaboost.name',
            description:
              'sections.iv-therapy.subcategories.iv.plans.iv-megaboost.description',
            price: 19900,
            buttonLabel:
              'sections.iv-therapy.subcategories.iv.plans.iv-megaboost.buttonLabel',
            buttonVariant: 'filled',
            shopifyVariantId:
              process.env['NEXT_PUBLIC_ENVIRONMENT'] === 'PRODUCTION'
                ? '44742040780870'
                : '55543517184073',
            learnMore: true,
            detail: {
              description:
                'sections.iv-therapy.subcategories.iv.plans.iv-megaboost.detail.description',
              imageUrl:
                'web_v2/performance-lab/pricing-page/placeholders/sidebar/megaboost.png',
              sections: [
                {
                  title:
                    'sections.iv-therapy.subcategories.iv.plans.iv-megaboost.detail.sections.0.title',
                  content:
                    'sections.iv-therapy.subcategories.iv.plans.iv-megaboost.detail.sections.0.content',
                },
                {
                  title:
                    'sections.iv-therapy.subcategories.iv.plans.iv-megaboost.detail.sections.1.title',
                  content:
                    'sections.iv-therapy.subcategories.iv.plans.iv-megaboost.detail.sections.1.content',
                },
                {
                  title:
                    'sections.iv-therapy.subcategories.iv.plans.iv-megaboost.detail.sections.2.title',
                  content:
                    'sections.iv-therapy.subcategories.iv.plans.iv-megaboost.detail.sections.2.content',
                },
              ],
            },
            thumbnailUrl:
              'web_v2/performance-lab/pricing-page/placeholders/iv/megaboost.png',
            // bulletPoints: [
            //   'sections.iv-therapy.subcategories.iv.plans.iv-megaboost.bulletPoints.0',
            //   'sections.iv-therapy.subcategories.iv.plans.iv-megaboost.bulletPoints.1',
            //   'sections.iv-therapy.subcategories.iv.plans.iv-megaboost.bulletPoints.2',
            //   'sections.iv-therapy.subcategories.iv.plans.iv-megaboost.bulletPoints.3',
            // ],
          },
          {
            id: 'iv-vitaglow',
            name: 'sections.iv-therapy.subcategories.iv.plans.iv-vitaglow.name',
            description:
              'sections.iv-therapy.subcategories.iv.plans.iv-vitaglow.description',
            price: 19900,
            buttonLabel:
              'sections.iv-therapy.subcategories.iv.plans.iv-vitaglow.buttonLabel',
            buttonVariant: 'filled',
            shopifyVariantId:
              process.env['NEXT_PUBLIC_ENVIRONMENT'] === 'PRODUCTION'
                ? '44742040813638'
                : '55543517184073',
            learnMore: true,
            detail: {
              description:
                'sections.iv-therapy.subcategories.iv.plans.iv-vitaglow.detail.description',
              imageUrl:
                'web_v2/performance-lab/pricing-page/placeholders/sidebar/vitaglow.png',
              sections: [
                {
                  title:
                    'sections.iv-therapy.subcategories.iv.plans.iv-vitaglow.detail.sections.0.title',
                  content:
                    'sections.iv-therapy.subcategories.iv.plans.iv-vitaglow.detail.sections.0.content',
                },
                {
                  title:
                    'sections.iv-therapy.subcategories.iv.plans.iv-vitaglow.detail.sections.1.title',
                  content:
                    'sections.iv-therapy.subcategories.iv.plans.iv-vitaglow.detail.sections.1.content',
                },
                {
                  title:
                    'sections.iv-therapy.subcategories.iv.plans.iv-vitaglow.detail.sections.2.title',
                  content:
                    'sections.iv-therapy.subcategories.iv.plans.iv-vitaglow.detail.sections.2.content',
                },
              ],
            },
            thumbnailUrl:
              'web_v2/performance-lab/pricing-page/placeholders/iv/vitaglow.png',
            // bulletPoints: [
            //   'sections.iv-therapy.subcategories.iv.plans.iv-vitaglow.bulletPoints.0',
            //   'sections.iv-therapy.subcategories.iv.plans.iv-vitaglow.bulletPoints.1',
            //   'sections.iv-therapy.subcategories.iv.plans.iv-vitaglow.bulletPoints.2',
            //   'sections.iv-therapy.subcategories.iv.plans.iv-vitaglow.bulletPoints.3',
            // ],
          },
          {
            id: 'iv-nad-125',
            name: 'sections.iv-therapy.subcategories.iv.plans.iv-nad-125.name',
            description:
              'sections.iv-therapy.subcategories.iv.plans.iv-nad-125.description',
            price: 29900,
            buttonLabel:
              'sections.iv-therapy.subcategories.iv.plans.iv-nad-125.buttonLabel',
            buttonVariant: 'filled',
            shopifyVariantId:
              process.env['NEXT_PUBLIC_ENVIRONMENT'] === 'PRODUCTION'
                ? '44742040846406'
                : '55543517184073',
            learnMore: true,
            detail: {
              imageUrl:
                'web_v2/performance-lab/pricing-page/placeholders/sidebar/nad+.png',
              description:
                'sections.iv-therapy.subcategories.iv.plans.iv-nad-125.detail.description',
              sections: [
                {
                  title:
                    'sections.iv-therapy.subcategories.iv.plans.iv-nad-125.detail.sections.0.title',
                  content:
                    'sections.iv-therapy.subcategories.iv.plans.iv-nad-125.detail.sections.0.content',
                },
                {
                  title:
                    'sections.iv-therapy.subcategories.iv.plans.iv-nad-125.detail.sections.1.title',
                  content:
                    'sections.iv-therapy.subcategories.iv.plans.iv-nad-125.detail.sections.1.content',
                },
                {
                  title:
                    'sections.iv-therapy.subcategories.iv.plans.iv-nad-125.detail.sections.2.title',
                  content:
                    'sections.iv-therapy.subcategories.iv.plans.iv-nad-125.detail.sections.2.content',
                },
              ],
            },
            thumbnailUrl:
              'web_v2/performance-lab/pricing-page/placeholders/iv/nad-125.png',
            // bulletPoints: [
            //   'sections.iv-therapy.subcategories.iv.plans.iv-nad-125.bulletPoints.0',
            // ],
          },
          // {
          //   id: 'iv-nad-250',
          //   name: 'sections.iv-therapy.subcategories.iv.plans.iv-nad-250.name',
          //   description:
          //     'sections.iv-therapy.subcategories.iv.plans.iv-nad-250.description',
          //   price: 49900,
          //   buttonLabel:
          //     'sections.iv-therapy.subcategories.iv.plans.iv-nad-250.buttonLabel',
          //   buttonVariant: 'filled',
          //   shopifyVariantId:
          //     process.env['NEXT_PUBLIC_ENVIRONMENT'] === 'PRODUCTION'
          //       ? '44742040879174'
          //       : '55543517184073',
          //   learnMore: true,
          //   detail: {
          //     description:
          //       'sections.iv-therapy.subcategories.iv.plans.iv-nad-250.detail.description',
          //     sections: [
          //       {
          //         title:
          //           'sections.iv-therapy.subcategories.iv.plans.iv-nad-250.detail.sections.0.title',
          //         content:
          //           'sections.iv-therapy.subcategories.iv.plans.iv-nad-250.detail.sections.0.content',
          //       },
          //       {
          //         title:
          //           'sections.iv-therapy.subcategories.iv.plans.iv-nad-250.detail.sections.1.title',
          //         content:
          //           'sections.iv-therapy.subcategories.iv.plans.iv-nad-250.detail.sections.1.content',
          //       },
          //       {
          //         title:
          //           'sections.iv-therapy.subcategories.iv.plans.iv-nad-250.detail.sections.2.title',
          //         content:
          //           'sections.iv-therapy.subcategories.iv.plans.iv-nad-250.detail.sections.2.content',
          //       },
          //     ],
          //   },
          //   thumbnailUrl:
          //     'web_v2/performance-lab/pricing-page/placeholders/iv/nad-250.png',
          //   bulletPoints: [
          //     'sections.iv-therapy.subcategories.iv.plans.iv-nad-250.bulletPoints.0',
          //   ],
          // },
          // {
          //   id: 'iv-nad-500',
          //   name: 'sections.iv-therapy.subcategories.iv.plans.iv-nad-500.name',
          //   description:
          //     'sections.iv-therapy.subcategories.iv.plans.iv-nad-500.description',
          //   price: 59900,
          //   buttonLabel:
          //     'sections.iv-therapy.subcategories.iv.plans.iv-nad-500.buttonLabel',
          //   buttonVariant: 'filled',
          //   shopifyVariantId:
          //     process.env['NEXT_PUBLIC_ENVIRONMENT'] === 'PRODUCTION'
          //       ? '44742040944710'
          //       : '55543517184073',
          //   learnMore: true,
          //   detail: {
          //     description:
          //       'sections.iv-therapy.subcategories.iv.plans.iv-nad-500.detail.description',
          //     sections: [
          //       {
          //         title:
          //           'sections.iv-therapy.subcategories.iv.plans.iv-nad-500.detail.sections.0.title',
          //         content:
          //           'sections.iv-therapy.subcategories.iv.plans.iv-nad-500.detail.sections.0.content',
          //       },
          //       {
          //         title:
          //           'sections.iv-therapy.subcategories.iv.plans.iv-nad-500.detail.sections.1.title',
          //         content:
          //           'sections.iv-therapy.subcategories.iv.plans.iv-nad-500.detail.sections.1.content',
          //       },
          //       {
          //         title:
          //           'sections.iv-therapy.subcategories.iv.plans.iv-nad-500.detail.sections.2.title',
          //         content:
          //           'sections.iv-therapy.subcategories.iv.plans.iv-nad-500.detail.sections.2.content',
          //       },
          //     ],
          //   },
          //   thumbnailUrl:
          //     'web_v2/performance-lab/pricing-page/placeholders/iv/nad-500.png',
          //   bulletPoints: [
          //     'sections.iv-therapy.subcategories.iv.plans.iv-nad-500.bulletPoints.0',
          //   ],
          // },
          {
            id: 'iv-ultraviv-pro',
            name: 'sections.iv-therapy.subcategories.iv.plans.iv-ultraviv-pro.name',
            description:
              'sections.iv-therapy.subcategories.iv.plans.iv-ultraviv-pro.description',
            price: 34900,
            buttonLabel:
              'sections.iv-therapy.subcategories.iv.plans.iv-ultraviv-pro.buttonLabel',
            buttonVariant: 'filled',
            shopifyVariantId:
              process.env['NEXT_PUBLIC_ENVIRONMENT'] === 'PRODUCTION'
                ? '44742042484806'
                : '55543517184073',
            learnMore: true,
            detail: {
              description:
                'sections.iv-therapy.subcategories.iv.plans.iv-ultraviv-pro.detail.description',
              imageUrl:
                'web_v2/performance-lab/pricing-page/placeholders/sidebar/ultraviv-pro.png',
              sections: [
                {
                  title:
                    'sections.iv-therapy.subcategories.iv.plans.iv-ultraviv-pro.detail.sections.0.title',
                  content:
                    'sections.iv-therapy.subcategories.iv.plans.iv-ultraviv-pro.detail.sections.0.content',
                },
                {
                  title:
                    'sections.iv-therapy.subcategories.iv.plans.iv-ultraviv-pro.detail.sections.1.title',
                  content:
                    'sections.iv-therapy.subcategories.iv.plans.iv-ultraviv-pro.detail.sections.1.content',
                },
                {
                  title:
                    'sections.iv-therapy.subcategories.iv.plans.iv-ultraviv-pro.detail.sections.2.title',
                  content:
                    'sections.iv-therapy.subcategories.iv.plans.iv-ultraviv-pro.detail.sections.2.content',
                },
              ],
            },
            thumbnailUrl:
              'web_v2/performance-lab/pricing-page/placeholders/iv/ultraviv-pro.png',
            // bulletPoints: [
            //   'sections.iv-therapy.subcategories.iv.plans.iv-ultraviv-pro.bulletPoints.0',
            //   'sections.iv-therapy.subcategories.iv.plans.iv-ultraviv-pro.bulletPoints.1',
            //   'sections.iv-therapy.subcategories.iv.plans.iv-ultraviv-pro.bulletPoints.2',
            //   'sections.iv-therapy.subcategories.iv.plans.iv-ultraviv-pro.bulletPoints.3',
            //   'sections.iv-therapy.subcategories.iv.plans.iv-ultraviv-pro.bulletPoints.4',
            //   'sections.iv-therapy.subcategories.iv.plans.iv-ultraviv-pro.bulletPoints.5',
            // ],
          },
          {
            id: 'iv-glutathione',
            name: 'sections.iv-therapy.subcategories.iv.plans.iv-glutathione.name',
            description:
              'sections.iv-therapy.subcategories.iv.plans.iv-glutathione.description',
            price: 7000,
            buttonLabel:
              'sections.iv-therapy.subcategories.iv.plans.iv-glutathione.buttonLabel',
            buttonVariant: 'filled',
            shopifyVariantId:
              process.env['NEXT_PUBLIC_ENVIRONMENT'] === 'PRODUCTION'
                ? '44742042845254'
                : '55543517184073',
            learnMore: true,
            detail: {
              description:
                'sections.iv-therapy.subcategories.iv.plans.iv-glutathione.detail.description',
              imageUrl:
                'web_v2/performance-lab/pricing-page/placeholders/sidebar/im-glutathione.png',
              sections: [
                {
                  title:
                    'sections.iv-therapy.subcategories.iv.plans.iv-glutathione.detail.sections.0.title',
                  content:
                    'sections.iv-therapy.subcategories.iv.plans.iv-glutathione.detail.sections.0.content',
                },
                {
                  title:
                    'sections.iv-therapy.subcategories.iv.plans.iv-glutathione.detail.sections.1.title',
                  content:
                    'sections.iv-therapy.subcategories.iv.plans.iv-glutathione.detail.sections.1.content',
                },
                {
                  title:
                    'sections.iv-therapy.subcategories.iv.plans.iv-glutathione.detail.sections.2.title',
                  content:
                    'sections.iv-therapy.subcategories.iv.plans.iv-glutathione.detail.sections.2.content',
                },
              ],
            },
            thumbnailUrl:
              'web_v2/performance-lab/pricing-page/placeholders/iv/gluthathione.png',
            // bulletPoints: [
            //   'sections.iv-therapy.subcategories.iv.plans.iv-glutathione.bulletPoints.0',
            // ],
          },
          {
            id: 'iv-royal-flush',
            name: 'sections.iv-therapy.subcategories.iv.plans.iv-royal-flush.name',
            description:
              'sections.iv-therapy.subcategories.iv.plans.iv-royal-flush.description',
            price: 0,
            // price: 49900,
            buttonLabel:
              'sections.iv-therapy.subcategories.iv.plans.iv-revive.buttonLabel',
            buttonVariant: 'filled',
            thumbnailUrl:
              'web_v2/performance-lab/pricing-page/placeholders/iv/royal-flush.png',
            learnMore: true,
            shopifyVariantId:
              process.env['NEXT_PUBLIC_ENVIRONMENT'] === 'PRODUCTION'
                ? '44742042517574'
                : '55543517184073',
            comingSoon: true,
            detail: {
              description:
                'sections.iv-therapy.subcategories.iv.plans.iv-royal-flush.detail.description',
              imageUrl:
                'web_v2/performance-lab/pricing-page/placeholders/sidebar/iv-royal-flush.png',
              sections: [
                {
                  title:
                    'sections.iv-therapy.subcategories.iv.plans.iv-royal-flush.detail.sections.0.title',
                  content:
                    'sections.iv-therapy.subcategories.iv.plans.iv-royal-flush.detail.sections.0.content',
                },
                {
                  title:
                    'sections.iv-therapy.subcategories.iv.plans.iv-royal-flush.detail.sections.1.title',
                  content:
                    'sections.iv-therapy.subcategories.iv.plans.iv-royal-flush.detail.sections.1.content',
                },
                {
                  title:
                    'sections.iv-therapy.subcategories.iv.plans.iv-royal-flush.detail.sections.2.title',
                  content:
                    'sections.iv-therapy.subcategories.iv.plans.iv-royal-flush.detail.sections.2.content',
                },
              ],
            },
          },
          {
            id: 'iv-revive',
            preserveCase: true,
            name: 'sections.iv-therapy.subcategories.iv.plans.iv-revive.name',
            description:
              'sections.iv-therapy.subcategories.iv.plans.iv-revive.description',
            price: 0,
            // price: 59900,
            buttonLabel:
              'sections.iv-therapy.subcategories.iv.plans.iv-revive.buttonLabel',
            buttonVariant: 'filled',
            thumbnailUrl:
              'web_v2/performance-lab/pricing-page/placeholders/iv/revive.png',
            learnMore: true,
            comingSoon: true,
            shopifyVariantId:
              process.env['NEXT_PUBLIC_ENVIRONMENT'] === 'PRODUCTION'
                ? '44742042550342'
                : '55543517184073',
            detail: {
              description:
                'sections.iv-therapy.subcategories.iv.plans.iv-revive.detail.description',
              imageUrl:
                'web_v2/performance-lab/pricing-page/placeholders/sidebar/iv-royal-flush.png',
              sections: [
                {
                  title:
                    'sections.iv-therapy.subcategories.iv.plans.iv-revive.detail.sections.0.title',
                  content:
                    'sections.iv-therapy.subcategories.iv.plans.iv-revive.detail.sections.0.content',
                },
                {
                  title:
                    'sections.iv-therapy.subcategories.iv.plans.iv-revive.detail.sections.1.title',
                  content:
                    'sections.iv-therapy.subcategories.iv.plans.iv-revive.detail.sections.1.content',
                },
                {
                  title:
                    'sections.iv-therapy.subcategories.iv.plans.iv-revive.detail.sections.2.title',
                  content:
                    'sections.iv-therapy.subcategories.iv.plans.iv-revive.detail.sections.2.content',
                },
              ],
            },
          },
          // {
          //   id: 'iv-personalized',
          //   name: 'sections.iv-therapy.subcategories.iv.plans.iv-personalized.name',
          //   description:
          //     'sections.iv-therapy.subcategories.iv.plans.iv-personalized.description',
          //   price: 0,
          //   buttonLabel:
          //     'sections.iv-therapy.subcategories.iv.plans.iv-personalized.buttonLabel',
          //   buttonVariant: 'outline',
          //   thumbnailUrl:
          //     'web_v2/performance-lab/pricing-page/placeholders/iv/miniboost.png',
          //   learnMore: true,
          //   detail: {
          //     description:
          //       'sections.iv-therapy.subcategories.iv.plans.iv-personalized.detail.description',
          //     sections: [
          //       {
          //         title:
          //           'sections.iv-therapy.subcategories.iv.plans.iv-personalized.detail.sections.0.title',
          //         content:
          //           'sections.iv-therapy.subcategories.iv.plans.iv-personalized.detail.sections.0.content',
          //       },
          //       {
          //         title:
          //           'sections.iv-therapy.subcategories.iv.plans.iv-personalized.detail.sections.1.title',
          //         content:
          //           'sections.iv-therapy.subcategories.iv.plans.iv-personalized.detail.sections.1.content',
          //       },
          //       {
          //         title:
          //           'sections.iv-therapy.subcategories.iv.plans.iv-personalized.detail.sections.2.title',
          //         content:
          //           'sections.iv-therapy.subcategories.iv.plans.iv-personalized.detail.sections.2.content',
          //       },
          //     ],
          //   },
          // },
        ],
      },
      {
        id: 'im',
        label: 'sections.iv-therapy.subcategories.im.label',
        plans: [
          // {
          //   id: 'pl-im-glutathione',
          //   name: 'sections.iv-therapy.subcategories.iv.plans.pl-im-glutathione.name',
          //   description:
          //     'sections.iv-therapy.subcategories.iv.plans.pl-im-glutathione.description',
          //   price: 7000,
          //   buttonLabel:
          //     'sections.iv-therapy.subcategories.iv.plans.pl-im-glutathione.buttonLabel',
          //   buttonVariant: 'filled',
          //   thumbnailUrl:
          //     'web_v2/performance-lab/pricing-page/placeholders/im/glutathione.png',
          //   learnMore: true,
          //   detail: {
          //     description:
          //       'sections.iv-therapy.subcategories.iv.plans.pl-im-glutathione.detail.description',
          //     imageUrl:
          //       'web_v2/performance-lab/pricing-page/placeholders/sidebar/im-glutathione.png',
          //     sections: [
          //       {
          //         title:
          //           'sections.iv-therapy.subcategories.iv.plans.pl-im-glutathione.detail.sections.0.title',
          //         content:
          //           'sections.iv-therapy.subcategories.iv.plans.pl-im-glutathione.detail.sections.0.content',
          //       },
          //       {
          //         title:
          //           'sections.iv-therapy.subcategories.iv.plans.pl-im-glutathione.detail.sections.1.title',
          //         content:
          //           'sections.iv-therapy.subcategories.iv.plans.pl-im-glutathione.detail.sections.1.content',
          //       },
          //       {
          //         title:
          //           'sections.iv-therapy.subcategories.iv.plans.pl-im-glutathione.detail.sections.2.title',
          //         content:
          //           'sections.iv-therapy.subcategories.iv.plans.pl-im-glutathione.detail.sections.2.content',
          //       },
          //     ],
          //   },
          //   shopifyVariantId:
          //     process.env['NEXT_PUBLIC_ENVIRONMENT'] === 'PRODUCTION'
          //       ? '44742042845254'
          //       : '55543517184073',
          // },
          {
            id: 'pl-im-b-family',
            name: 'sections.iv-therapy.subcategories.im.plans.pl-im-b-family.name',
            description:
              'sections.iv-therapy.subcategories.im.plans.pl-im-b-family.description',
            price: 2900,
            buttonLabel:
              'sections.iv-therapy.subcategories.im.plans.pl-im-b-family.buttonLabel',
            buttonVariant: 'filled',
            thumbnailUrl:
              'web_v2/performance-lab/pricing-page/placeholders/im/vitamin-b.png',
            learnMore: true,
            detail: {
              description:
                'sections.iv-therapy.subcategories.im.plans.pl-im-b-family.detail.description',
              imageUrl:
                'web_v2/performance-lab/pricing-page/placeholders/sidebar/b-family.png',
              sections: [
                {
                  title:
                    'sections.iv-therapy.subcategories.im.plans.pl-im-b-family.detail.sections.0.title',
                  content:
                    'sections.iv-therapy.subcategories.im.plans.pl-im-b-family.detail.sections.0.content',
                },
                {
                  title:
                    'sections.iv-therapy.subcategories.im.plans.pl-im-b-family.detail.sections.1.title',
                  content:
                    'sections.iv-therapy.subcategories.im.plans.pl-im-b-family.detail.sections.1.content',
                },
                {
                  title:
                    'sections.iv-therapy.subcategories.im.plans.pl-im-b-family.detail.sections.2.title',
                  content:
                    'sections.iv-therapy.subcategories.im.plans.pl-im-b-family.detail.sections.2.content',
                },
              ],
            },
            shopifyVariantId:
              process.env['NEXT_PUBLIC_ENVIRONMENT'] === 'PRODUCTION'
                ? '44742042648646'
                : '55543517184073',
          },
          {
            id: 'pl-im-vitamin-c',
            name: 'sections.iv-therapy.subcategories.im.plans.pl-im-vitamin-c.name',
            description:
              'sections.iv-therapy.subcategories.im.plans.pl-im-vitamin-c.description',
            price: 2900,
            buttonLabel:
              'sections.iv-therapy.subcategories.im.plans.pl-im-vitamin-c.buttonLabel',
            buttonVariant: 'filled',
            thumbnailUrl:
              'web_v2/performance-lab/pricing-page/placeholders/im/vitamin-c.png',
            learnMore: true,
            detail: {
              description:
                'sections.iv-therapy.subcategories.im.plans.pl-im-vitamin-c.detail.description',
              imageUrl:
                'web_v2/performance-lab/pricing-page/placeholders/sidebar/vitamin-c.png',
              sections: [
                {
                  title:
                    'sections.iv-therapy.subcategories.im.plans.pl-im-vitamin-c.detail.sections.0.title',
                  content:
                    'sections.iv-therapy.subcategories.im.plans.pl-im-vitamin-c.detail.sections.0.content',
                },
                {
                  title:
                    'sections.iv-therapy.subcategories.im.plans.pl-im-vitamin-c.detail.sections.1.title',
                  content:
                    'sections.iv-therapy.subcategories.im.plans.pl-im-vitamin-c.detail.sections.1.content',
                },
                {
                  title:
                    'sections.iv-therapy.subcategories.im.plans.pl-im-vitamin-c.detail.sections.2.title',
                  content:
                    'sections.iv-therapy.subcategories.im.plans.pl-im-vitamin-c.detail.sections.2.content',
                },
              ],
            },
            shopifyVariantId:
              process.env['NEXT_PUBLIC_ENVIRONMENT'] === 'PRODUCTION'
                ? '44742042681414'
                : '55543517184073',
          },
          {
            id: 'pl-im-vitamin-d',
            name: 'sections.iv-therapy.subcategories.im.plans.pl-im-vitamin-d.name',
            description:
              'sections.iv-therapy.subcategories.im.plans.pl-im-vitamin-d.description',
            price: 2900,
            buttonLabel:
              'sections.iv-therapy.subcategories.im.plans.pl-im-vitamin-d.buttonLabel',
            buttonVariant: 'filled',
            thumbnailUrl:
              'web_v2/performance-lab/pricing-page/placeholders/im/vitamin-d.png',
            learnMore: true,
            detail: {
              description:
                'sections.iv-therapy.subcategories.im.plans.pl-im-vitamin-d.detail.description',
              imageUrl:
                'web_v2/performance-lab/pricing-page/placeholders/sidebar/vitamin-d.png',
              sections: [
                {
                  title:
                    'sections.iv-therapy.subcategories.im.plans.pl-im-vitamin-d.detail.sections.0.title',
                  content:
                    'sections.iv-therapy.subcategories.im.plans.pl-im-vitamin-d.detail.sections.0.content',
                },
                {
                  title:
                    'sections.iv-therapy.subcategories.im.plans.pl-im-vitamin-d.detail.sections.1.title',
                  content:
                    'sections.iv-therapy.subcategories.im.plans.pl-im-vitamin-d.detail.sections.1.content',
                },
                {
                  title:
                    'sections.iv-therapy.subcategories.im.plans.pl-im-vitamin-d.detail.sections.2.title',
                  content:
                    'sections.iv-therapy.subcategories.im.plans.pl-im-vitamin-d.detail.sections.2.content',
                },
              ],
            },
            shopifyVariantId:
              process.env['NEXT_PUBLIC_ENVIRONMENT'] === 'PRODUCTION'
                ? '44742042746950'
                : '55543517184073',
          },
          {
            id: 'pl-im-vitaboost',
            name: 'sections.iv-therapy.subcategories.im.plans.pl-im-vitaboost.name',
            description:
              'sections.iv-therapy.subcategories.im.plans.pl-im-vitaboost.description',
            price: 3900,
            buttonLabel:
              'sections.iv-therapy.subcategories.im.plans.pl-im-vitaboost.buttonLabel',
            buttonVariant: 'filled',
            thumbnailUrl:
              'web_v2/performance-lab/pricing-page/placeholders/im/vitaboost.png',
            learnMore: true,
            detail: {
              description:
                'sections.iv-therapy.subcategories.im.plans.pl-im-vitaboost.detail.description',
              imageUrl:
                'web_v2/performance-lab/pricing-page/placeholders/sidebar/vitaboost.png',
              sections: [
                {
                  title:
                    'sections.iv-therapy.subcategories.im.plans.pl-im-vitaboost.detail.sections.0.title',
                  content:
                    'sections.iv-therapy.subcategories.im.plans.pl-im-vitaboost.detail.sections.0.content',
                },
                {
                  title:
                    'sections.iv-therapy.subcategories.im.plans.pl-im-vitaboost.detail.sections.1.title',
                  content:
                    'sections.iv-therapy.subcategories.im.plans.pl-im-vitaboost.detail.sections.1.content',
                },
                {
                  title:
                    'sections.iv-therapy.subcategories.im.plans.pl-im-vitaboost.detail.sections.2.title',
                  content:
                    'sections.iv-therapy.subcategories.im.plans.pl-im-vitaboost.detail.sections.2.content',
                },
              ],
            },
            shopifyVariantId:
              process.env['NEXT_PUBLIC_ENVIRONMENT'] === 'PRODUCTION'
                ? '44742042812486'
                : '55543517184073',
          },
          {
            id: 'pl-im-nad',
            name: 'sections.iv-therapy.subcategories.im.plans.pl-im-nad.name',
            description:
              'sections.iv-therapy.subcategories.im.plans.pl-im-nad.description',
            price: 11900,
            buttonLabel:
              'sections.iv-therapy.subcategories.im.plans.pl-im-nad.buttonLabel',
            buttonVariant: 'filled',
            thumbnailUrl:
              'web_v2/performance-lab/pricing-page/placeholders/im/nad-im.png',
            learnMore: true,
            detail: {
              description:
                'sections.iv-therapy.subcategories.im.plans.pl-im-nad.detail.description',
              imageUrl:
                'web_v2/performance-lab/pricing-page/placeholders/sidebar/im-nad+.png',
              sections: [
                {
                  title:
                    'sections.iv-therapy.subcategories.im.plans.pl-im-nad.detail.sections.0.title',
                  content:
                    'sections.iv-therapy.subcategories.im.plans.pl-im-nad.detail.sections.0.content',
                },
                {
                  title:
                    'sections.iv-therapy.subcategories.im.plans.pl-im-nad.detail.sections.1.title',
                  content:
                    'sections.iv-therapy.subcategories.im.plans.pl-im-nad.detail.sections.1.content',
                },
                {
                  title:
                    'sections.iv-therapy.subcategories.im.plans.pl-im-nad.detail.sections.2.title',
                  content:
                    'sections.iv-therapy.subcategories.im.plans.pl-im-nad.detail.sections.2.content',
                },
              ],
            },
            shopifyVariantId:
              process.env['NEXT_PUBLIC_ENVIRONMENT'] === 'PRODUCTION'
                ? '44742043238470'
                : '55543517184073',
          },
          // {
          //   id: 'pl-im-slimboost',
          //   name: 'sections.iv-therapy.subcategories.im.plans.pl-im-slimboost.name',
          //   description:
          //     'sections.iv-therapy.subcategories.im.plans.pl-im-slimboost.description',
          //   price: 3900,
          //   buttonLabel:
          //     'sections.iv-therapy.subcategories.im.plans.pl-im-slimboost.buttonLabel',
          //   buttonVariant: 'outline',
          //   thumbnailUrl:
          //     'web_v2/performance-lab/pricing-page/placeholders/im/slimboost.png',
          //   learnMore: true,
          //   detail: {
          //     description:
          //       'sections.iv-therapy.subcategories.im.plans.pl-im-slimboost.detail.description',
          //     sections: [
          //       {
          //         title:
          //           'sections.iv-therapy.subcategories.im.plans.pl-im-slimboost.detail.sections.0.title',
          //         content:
          //           'sections.iv-therapy.subcategories.im.plans.pl-im-slimboost.detail.sections.0.content',
          //       },
          //       {
          //         title:
          //           'sections.iv-therapy.subcategories.im.plans.pl-im-slimboost.detail.sections.1.title',
          //         content:
          //           'sections.iv-therapy.subcategories.im.plans.pl-im-slimboost.detail.sections.1.content',
          //       },
          //       {
          //         title:
          //           'sections.iv-therapy.subcategories.im.plans.pl-im-slimboost.detail.sections.2.title',
          //         content:
          //           'sections.iv-therapy.subcategories.im.plans.pl-im-slimboost.detail.sections.2.content',
          //       },
          //     ],
          //   },
          // },
        ],
      },
    ],
  },

  // ─── 5. GOLD PLAN ──────────────────────────────────────────────────
  {
    id: 'gold',
    sidebarLabel: 'sections.gold.sidebarLabel',
    plansTabLabel: 'sections.gold.plansTabLabel',
    heroTitle: 'sections.gold.heroTitle',
    heroImageUrl:
      'web_v2/performance-lab/pricing-page/placeholders/heroes/gold-1.png',
    heroDescription: 'sections.gold.heroDescription',
    heroTextPosition: 'left',
    cardLayout: '2-col-large',
    compareHeaders: [
      'sections.gold.compareHeaders.0',
      'sections.gold.compareHeaders.1',
    ],
    plans: [
      {
        id: 'gold-pro',
        name: 'sections.gold.plans.gold-pro.name',
        description: 'sections.gold.plans.gold-pro.description',
        price: 89999,
        buttonLabel: 'sections.gold.plans.gold-pro.buttonLabel',
        buttonVariant: 'filled',
        thumbnailUrl:
          'web_v2/performance-lab/pricing-page/placeholders/gold/gold-pro.png',
        tag: 'sections.gold.plans.gold-pro.tag',
        shopifyVariantId:
          process.env['NEXT_PUBLIC_ENVIRONMENT'] === 'PRODUCTION'
            ? '44742040223814'
            : '55543514660937',
        sessionInfo: 'sections.gold.plans.gold-pro.sessionInfo',
        validity: 'sections.gold.plans.gold-pro.validity',
        includesHeader: 'sections.gold.plans.gold-pro.includesHeader',
        includes: [
          {
            label: 'sections.gold.plans.gold-pro.includes.14.label',
            detail: visionDashboardDetail,
          },
          {
            label: 'sections.gold.plans.gold-pro.includes.0.label',
            detail: bloodVisionPeakDetail,
          },
          {
            label: 'sections.gold.plans.gold-pro.includes.1.label',
            detail: bodyCompositionDetail,
          },
          {
            label: 'sections.gold.plans.gold-pro.includes.2.label',
            detail: boneDensityDetail,
          },
          {
            label: 'sections.gold.plans.gold-pro.includes.3.label',
            detail: ecgDetail,
          },
          {
            label: 'sections.gold.plans.gold-pro.includes.4.label',
            detail: vitalsDetail,
          },
          {
            label: 'sections.gold.plans.gold-pro.includes.5.label',
            detail: oralHealthDetail,
          },
          {
            label: 'sections.gold.plans.gold-pro.includes.6.label',
            detail: cognitionDetail,
          },
          {
            label: 'sections.gold.plans.gold-pro.includes.7.label',
            detail: vo2MaxTreadmillDetail,
          },
          {
            label: 'sections.gold.plans.gold-pro.includes.8.label',
            detail: musculoskeletalProDetail,
          },
          {
            label: 'sections.gold.plans.gold-pro.includes.9.label',
            detail: lungHealthDetail,
          },
          {
            label: 'sections.gold.plans.gold-pro.includes.10.label',
            detail: hearingVisionDetail,
          },
          {
            label: 'sections.gold.plans.gold-pro.includes.11.label',
            detail: nutritionDetail,
          },
          {
            label: 'sections.gold.plans.gold-pro.includes.12.label',
            detail: doctorConsultationDetail,
          },
          {
            label: 'sections.gold.plans.gold-pro.includes.13.label',
            detail: recoveryPassDetail,
          },
        ],
      },
      {
        id: 'gold-elite',
        name: 'sections.gold.plans.gold-elite.name',
        description: 'sections.gold.plans.gold-elite.description',
        price: 129999,
        buttonLabel: 'sections.gold.plans.gold-elite.buttonLabel',
        buttonVariant: 'filled',
        thumbnailUrl:
          'web_v2/performance-lab/pricing-page/placeholders/gold/gold-elite.png',
        shopifyVariantId:
          process.env['NEXT_PUBLIC_ENVIRONMENT'] === 'PRODUCTION'
            ? '44742040289350'
            : '55543514660937',

        sessionInfo: 'sections.gold.plans.gold-elite.sessionInfo',
        validity: 'sections.gold.plans.gold-elite.validity',
        includesHeader: 'sections.gold.plans.gold-elite.includesHeader',
        secondaryIncludesHeader:
          'sections.gold.plans.gold-elite.secondaryIncludesHeader',
        secondaryIncludes: [
          {
            label: 'sections.gold.plans.gold-pro.includes.14.label',
            detail: visionDashboardDetail,
          },
          {
            label: 'sections.gold.plans.gold-pro.includes.0.label',
            detail: bloodVisionPeakDetail,
          },
          {
            label: 'sections.gold.plans.gold-pro.includes.1.label',
            detail: bodyCompositionDetail,
          },
          {
            label: 'sections.gold.plans.gold-pro.includes.2.label',
            detail: boneDensityDetail,
          },
          {
            label: 'sections.gold.plans.gold-pro.includes.3.label',
            detail: ecgDetail,
          },
          {
            label: 'sections.gold.plans.gold-pro.includes.4.label',
            detail: vitalsDetail,
          },
          {
            label: 'sections.gold.plans.gold-pro.includes.5.label',
            detail: oralHealthDetail,
          },
          {
            label: 'sections.gold.plans.gold-pro.includes.6.label',
            detail: cognitionDetail,
          },
          {
            label: 'sections.gold.plans.gold-pro.includes.7.label',
            detail: vo2MaxTreadmillDetail,
          },
          {
            label: 'sections.gold.plans.gold-pro.includes.8.label',
            detail: musculoskeletalProDetail,
          },
          {
            label: 'sections.gold.plans.gold-pro.includes.9.label',
            detail: lungHealthDetail,
          },
          {
            label: 'sections.gold.plans.gold-pro.includes.10.label',
            detail: hearingVisionDetail,
          },
          {
            label: 'sections.gold.plans.gold-pro.includes.11.label',
            detail: nutritionDetail,
          },
          {
            label: 'sections.gold.plans.gold-pro.includes.12.label',
            detail: doctorConsultationDetail,
          },
          {
            label: 'sections.gold.plans.gold-pro.includes.13.label',
            detail: recoveryPassDetail,
          },
        ],
        includes: [
          {
            label: 'sections.gold.plans.gold-elite.includes.1.label',
            detail: nutriDNADetail,
          },
          {
            label: 'sections.gold.plans.gold-elite.includes.2.label',
            detail: gutMicrobiomeDetail,
          },
          // {
          //   label: 'sections.gold.plans.gold-elite.includes.3.label',
          //   detail: doctorConsultationDetail,
          // },
          // {
          //   label: 'sections.gold.plans.gold-elite.includes.4.label',
          //   detail: genomicHealthDetail,
          // },
          // {
          //   label: 'sections.gold.plans.gold-elite.includes.5.label',
          //   detail: genomicHealthDetail,
          // },
          // {
          //   label: 'sections.gold.plans.gold-elite.includes.6.label',
          //   detail: genomicHealthDetail,
          // },
        ],
      },
    ],
    compareData: [
      {
        name: 'sections.gold.compareData.0.name',
        thumbnailUrl:
          'web_v2/performance-lab/pricing-page/placeholders/diagnostics/blood-test.png',
        items: [
          {
            name: 'sections.gold.compareData.0.items.0.name',
            columns: [
              'sections.gold.compareData.0.items.0.columns.0',
              'sections.gold.compareData.0.items.0.columns.1',
            ],
          },
        ],
      },
      {
        name: 'sections.gold.compareData.1.name',
        thumbnailUrl:
          'web_v2/performance-lab/pricing-page/placeholders/diagnostics/body-composition-analysis.png',
        items: [
          {
            name: 'sections.gold.compareData.1.items.0.name',
            columns: [true, true],
          },
          {
            name: 'sections.gold.compareData.1.items.1.name',
            columns: [true, true],
          },
          {
            name: 'sections.gold.compareData.1.items.2.name',
            columns: [true, true],
          },
          {
            name: 'sections.gold.compareData.1.items.3.name',
            columns: [true, true],
          },
          {
            name: 'sections.gold.compareData.1.items.4.name',
            columns: [true, true],
          },
        ],
      },
      {
        name: 'sections.gold.compareData.2.name',
        thumbnailUrl:
          'web_v2/performance-lab/pricing-page/placeholders/diagnostics/bone-density.png',
        items: [
          {
            name: 'sections.gold.compareData.2.items.0.name',
            columns: [true, true],
          },
          {
            name: 'sections.gold.compareData.2.items.1.name',
            columns: [true, true],
          },
        ],
      },
      {
        name: 'sections.gold.compareData.3.name',
        thumbnailUrl:
          'web_v2/performance-lab/pricing-page/placeholders/diagnostics/ecg.png',
        items: [
          {
            name: 'sections.gold.compareData.3.items.0.name',
            columns: [true, true],
          },
          {
            name: 'sections.gold.compareData.3.items.1.name',
            columns: [true, true],
          },
        ],
      },
      {
        name: 'sections.gold.compareData.4.name',
        thumbnailUrl:
          'web_v2/performance-lab/pricing-page/placeholders/diagnostics/vitals.png',
        items: [
          {
            name: 'sections.gold.compareData.4.items.0.name',
            columns: [true, true],
          },
          {
            name: 'sections.gold.compareData.4.items.1.name',
            columns: [true, true],
          },
          {
            name: 'sections.gold.compareData.4.items.2.name',
            columns: [true, true],
          },
          {
            name: 'sections.gold.compareData.4.items.3.name',
            columns: [true, true],
          },
          {
            name: 'sections.gold.compareData.4.items.4.name',
            columns: [true, true],
          },
          {
            name: 'sections.gold.compareData.4.items.5.name',
            columns: [true, true],
          },
          {
            name: 'sections.gold.compareData.4.items.6.name',
            columns: [true, true],
          },
          {
            name: 'sections.gold.compareData.4.items.7.name',
            columns: [true, true],
          },
        ],
      },
      {
        name: 'sections.gold.compareData.5.name',
        thumbnailUrl:
          'web_v2/performance-lab/pricing-page/placeholders/diagnostics/oral-health.png',
        items: [
          {
            name: 'sections.gold.compareData.5.items.0.name',
            columns: [true, true],
          },
          {
            name: 'sections.gold.compareData.5.items.1.name',
            columns: [true, true],
          },
        ],
      },
      {
        name: 'sections.gold.compareData.6.name',
        thumbnailUrl:
          'web_v2/performance-lab/pricing-page/placeholders/diagnostics/cognition.png',
        items: [
          {
            name: 'sections.gold.compareData.6.items.0.name',
            columns: [true, true],
          },
          {
            name: 'sections.gold.compareData.6.items.1.name',
            columns: [true, true],
          },
          {
            name: 'sections.gold.compareData.6.items.2.name',
            columns: [true, true],
          },
          {
            name: 'sections.gold.compareData.6.items.3.name',
            columns: [true, true],
          },
          {
            name: 'sections.gold.compareData.6.items.4.name',
            columns: [true, true],
          },
        ],
      },
      {
        name: 'sections.gold.compareData.7.name',
        thumbnailUrl:
          'web_v2/performance-lab/pricing-page/placeholders/diagnostics/vo2-max.png',
        items: [
          {
            name: 'sections.gold.compareData.7.items.0.name',
            columns: [true, true],
          },
          {
            name: 'sections.gold.compareData.7.items.1.name',
            columns: [true, true],
          },
          {
            name: 'sections.gold.compareData.7.items.2.name',
            columns: [true, true],
          },
          {
            name: 'sections.gold.compareData.7.items.3.name',
            columns: [true, true],
          },
          {
            name: 'sections.gold.compareData.7.items.4.name',
            columns: [true, true],
          },
        ],
      },
      {
        name: 'sections.gold.compareData.8.name',
        thumbnailUrl:
          'web_v2/performance-lab/pricing-page/placeholders/diagnostics/musculoskeletal-assessment.png',
        items: [
          {
            name: 'sections.gold.compareData.8.items.0.name',
            columns: [true, true],
          },
          {
            name: 'sections.gold.compareData.8.items.1.name',
            columns: [true, true],
          },
          {
            name: 'sections.gold.compareData.8.items.2.name',
            columns: [true, true],
          },
          {
            name: 'sections.gold.compareData.8.items.3.name',
            columns: [true, true],
          },
          {
            name: 'sections.gold.compareData.8.items.4.name',
            columns: [true, true],
          },
        ],
      },
      {
        name: 'sections.gold.compareData.9.name',
        thumbnailUrl:
          'web_v2/performance-lab/pricing-page/placeholders/diagnostics/pulmonary-function-test.png',
        items: [
          {
            name: 'sections.gold.compareData.9.items.0.name',
            columns: [true, true],
          },
          {
            name: 'sections.gold.compareData.9.items.1.name',
            columns: [true, true],
          },
        ],
      },
      {
        name: 'sections.gold.compareData.10.name',
        thumbnailUrl:
          'web_v2/performance-lab/pricing-page/placeholders/diagnostics/hearing.png',
        items: [
          {
            name: 'sections.gold.compareData.10.items.0.name',
            columns: [true, true],
          },
          {
            name: 'sections.gold.compareData.10.items.1.name',
            columns: [true, true],
          },
        ],
      },
      {
        name: 'sections.gold.compareData.11.name',
        thumbnailUrl:
          'web_v2/performance-lab/pricing-page/placeholders/diagnostics/vision.png',
        items: [
          {
            name: 'sections.gold.compareData.11.items.0.name',
            columns: [true, true],
          },
          {
            name: 'sections.gold.compareData.11.items.1.name',
            columns: [true, true],
          },
          {
            name: 'sections.gold.compareData.11.items.2.name',
            columns: [true, true],
          },
          {
            name: 'sections.gold.compareData.11.items.3.name',
            columns: [true, true],
          },
          {
            name: 'sections.gold.compareData.11.items.4.name',
            columns: [true, true],
          },
        ],
      },
      {
        name: 'sections.gold.compareData.12.name',
        thumbnailUrl:
          'web_v2/performance-lab/pricing-page/placeholders/diagnostics/nutrition.png',
        items: [
          {
            name: 'sections.gold.compareData.12.items.0.name',
            columns: [true, true],
          },
          {
            name: 'sections.gold.compareData.12.items.1.name',
            columns: [true, true],
          },
          {
            name: 'sections.gold.compareData.12.items.2.name',
            columns: [true, true],
          },
          {
            name: 'sections.gold.compareData.12.items.3.name',
            columns: [true, true],
          },
          {
            name: 'sections.gold.compareData.12.items.4.name',
            columns: [true, true],
          },
        ],
      },
      {
        name: 'sections.gold.compareData.13.name',
        thumbnailUrl:
          'web_v2/performance-lab/pricing-page/placeholders/diagnostics/doctor-consultation.png',
        items: [
          {
            name: 'sections.gold.compareData.13.items.0.name',
            columns: [true, true],
          },
          {
            name: 'sections.gold.compareData.13.items.1.name',
            columns: [
              'sections.gold.compareData.13.items.1.columns.0',
              'sections.gold.compareData.13.items.1.columns.1',
            ],
          },
          {
            name: 'sections.gold.compareData.13.items.2.name',
            columns: [true, true],
          },
        ],
      },
      {
        name: 'sections.gold.compareData.14.name',
        thumbnailUrl:
          'web_v2/performance-lab/pricing-page/placeholders/diagnostics/genetic-testing.png',
        items: [
          {
            name: 'sections.gold.compareData.14.items.0.name',
            columns: [false, true],
          },
          {
            name: 'sections.gold.compareData.14.items.1.name',
            columns: [false, true],
          },
          {
            name: 'sections.gold.compareData.14.items.2.name',
            columns: [false, true],
          },
          {
            name: 'sections.gold.compareData.14.items.3.name',
            columns: [false, true],
          },
          {
            name: 'sections.gold.compareData.14.items.4.name',
            columns: [false, true],
          },
          {
            name: 'sections.gold.compareData.14.items.5.name',
            columns: [false, true],
          },
          {
            name: 'sections.gold.compareData.14.items.6.name',
            columns: [false, true],
          },
          {
            name: 'sections.gold.compareData.14.items.7.name',
            columns: [false, true],
          },
          {
            name: 'sections.gold.compareData.14.items.8.name',
            columns: [false, true],
          },
          {
            name: 'sections.gold.compareData.14.items.9.name',
            columns: [false, true],
          },
          {
            name: 'sections.gold.compareData.14.items.10.name',
            columns: [false, true],
          },
          {
            name: 'sections.gold.compareData.14.items.11.name',
            columns: [false, true],
          },
          {
            name: 'sections.gold.compareData.14.items.12.name',
            columns: [false, true],
          },
          {
            name: 'sections.gold.compareData.14.items.13.name',
            columns: [false, true],
          },
          {
            name: 'sections.gold.compareData.14.items.14.name',
            columns: [false, true],
          },
        ],
      },
      {
        name: 'sections.gold.compareData.15.name',
        thumbnailUrl:
          'web_v2/performance-lab/pricing-page/placeholders/diagnostics/gut-microbiome.png',
        items: [
          {
            name: 'sections.gold.compareData.15.items.0.name',
            columns: [false, true],
          },
          {
            name: 'sections.gold.compareData.15.items.1.name',
            columns: [false, true],
          },
          {
            name: 'sections.gold.compareData.15.items.2.name',
            columns: [false, true],
          },
          {
            name: 'sections.gold.compareData.15.items.3.name',
            columns: [false, true],
          },
          {
            name: 'sections.gold.compareData.15.items.4.name',
            columns: [false, true],
          },
          {
            name: 'sections.gold.compareData.15.items.5.name',
            columns: [false, true],
          },
          {
            name: 'sections.gold.compareData.15.items.6.name',
            columns: [false, true],
          },
          {
            name: 'sections.gold.compareData.15.items.7.name',
            columns: [false, true],
          },
          {
            name: 'sections.gold.compareData.15.items.8.name',
            columns: [false, true],
          },
          {
            name: 'sections.gold.compareData.15.items.9.name',
            columns: [false, true],
          },
          {
            name: 'sections.gold.compareData.15.items.10.name',
            columns: [false, true],
          },
        ],
      },
      {
        name: 'sections.gold.compareData.16.name',
        thumbnailUrl:
          'web_v2/performance-lab/pricing-page/placeholders/diagnostics/recovery-services.png',
        items: [
          {
            name: 'sections.gold.compareData.17.items.0.name',
            columns: [
              'sections.gold.compareData.17.items.0.columns.0',
              'sections.gold.compareData.17.items.0.columns.1',
            ],
          },
          {
            name: 'sections.gold.compareData.17.items.1.name',
            columns: [
              'sections.gold.compareData.17.items.1.columns.0',
              'sections.gold.compareData.17.items.1.columns.1',
            ],
          },
          {
            name: 'sections.gold.compareData.16.items.0.name',
            columns: [true, true],
          },
          {
            name: 'sections.gold.compareData.16.items.1.name',
            columns: [true, true],
          },
          {
            name: 'sections.gold.compareData.16.items.2.name',
            columns: [true, true],
          },
          {
            name: 'sections.gold.compareData.16.items.3.name',
            columns: [true, true],
          },
          {
            name: 'sections.gold.compareData.16.items.4.name',
            columns: [true, true],
          },
          {
            name: 'sections.gold.compareData.16.items.5.name',
            columns: [true, true],
          },
          {
            name: 'sections.gold.compareData.16.items.6.name',
            columns: [true, true],
          },
        ],
      },
      {
        name: 'sections.gold.compareData.18.name',
        thumbnailUrl:
          'web_v2/performance-lab/pricing-page/placeholders/diagnostics/doctor-consultation.png',
        items: [
          {
            name: 'sections.gold.compareData.18.items.0.name',
            columns: [true, true],
          },
        ],
      },
    ],
  },
];

export const planSections: PerformanceLabSection[] =
  withMonsoonOffers(planSectionsBase);
